/**
 * Dicionário de textos em português do Brasil, organizado por tela.
 *
 * Cada primeiro nível é uma tela do jogo, na ordem em que o jogador a encontra,
 * mais um bloco `comum` para o que aparece em mais de uma. A chave descreve o
 * lugar, não o texto: assim trocar a redação não obriga a mexer na tela.
 *
 * Marcadores de variável ficam entre chaves simples, como em `{clube}`. A função
 * `t` troca cada marcador pelo valor recebido e deixa intacto o que não vier.
 *
 * O `as const` no fim é o que faz o TypeScript conhecer cada chave, uma por uma:
 * é dele que sai o tipo `Chave`, e é por ele que uma chave inexistente vira erro
 * de compilação em vez de texto faltando na tela.
 */
export const ptBR = {
  // -------------------------------------------------------------------------
  // Comum a várias telas
  // -------------------------------------------------------------------------
  tabela: {
    buscar: "Buscar",
    ordenarPor: "Ordenar por {coluna}",
    nadaEncontrado: "Nada encontrado.",
  },

  comum: {
    marcaLinha1: "LEGACY",
    marcaLinha2: "MANAGER",
    /** O nome do produto inteiro, para o emblema de quem usa leitor de tela. */
    marca: "Legacy Manager",

    fechar: "Fechar",
    cancelar: "Cancelar",
    continuar: "Continuar",
    voltar: "Voltar",
    naFila: "na fila",
    vazio: "-",
    // O jogador sem contrato e o clube que sumiu do mundo. As duas moram aqui
    // porque a ficha, o mercado e a lista de propostas escrevem a mesma coisa.
    semClube: "Sem clube",
    clubeDesconhecido: "outro clube",
    condicao: "Condição",
    estrelas: "{marcadas} de {total} estrelas",
    moral: "Moral {valor}",
    semJogos: "Sem jogos",
    pontos: "pontos",

    /** Nome curto de cada característica, para os destaques do jogador. */
    caracteristicas: {
      arm: "Armação",
      cab: "Cabeceio",
      cru: "Cruzamento",
      des: "Desarme",
      dri: "Drible",
      fin: "Finalização",
      mar: "Marcação",
      pas: "Passe",
      res: "Resistência",
      vel: "Velocidade",
      col: "Colocação",
      sai: "Saída do gol",
      ref: "Reflexo",
      pen: "Pênalti",
    },

    setores: {
      GOL: "Goleiros",
      DEF: "Defensores",
      MEI: "Meio-campistas",
      ATA: "Atacantes",
    },

    /** Moeda do jogo, em formato compacto acima de mil. */
    dinheiro: {
      negativo: "menos ",
      bilhoes: "{sinal}$ {valor} bi",
      milhoes: "{sinal}$ {valor} mi",
      milhares: "{sinal}$ {valor} mil",
      unidade: "{sinal}$ {valor}",
    },

    /** Data de exibição derivada da rodada. O motor conta rodadas, não dias. */
    data: {
      rodada: "{dia} {mes} {ano} · {diaDaSemana}",
      diasDaSemana: {
        domingo: "Domingo",
        segunda: "Segunda",
        terca: "Terça",
        quarta: "Quarta",
        quinta: "Quinta",
        sexta: "Sexta",
        sabado: "Sábado",
      },
      meses: {
        jan: "Jan",
        fev: "Fev",
        mar: "Mar",
        abr: "Abr",
        mai: "Mai",
        jun: "Jun",
        jul: "Jul",
        ago: "Ago",
        set: "Set",
        out: "Out",
        nov: "Nov",
        dez: "Dez",
      },
    },

    /** Quanto tempo faz, para a lista de carreiras salvas. */
    tempo: {
      agora: "agora",
      minutos: "há {valor} min",
      horas: "há {valor} h",
      dias: "há {valor} dia(s)",

      /** Contagem regressiva do prazo da rodada, no modo online. */
      acabou: "prazo vencido",
      contagemDias: "{dias}d {horas}h",
      contagemHoras: "{horas}h {minutos}min",
      contagemMinutos: "{minutos}min {segundos}s",
    },
  },

  // -------------------------------------------------------------------------
  // Modo online, o que vale em qualquer tela dele
  // -------------------------------------------------------------------------
  online: {
    carregando: "Carregando...",
    /*
     * O servidor manda a mensagem de erro pronta, em português, e é ela que a
     * tela mostra. Este texto cobre só o que não veio da API, como uma falha
     * dentro do próprio navegador.
     */
    erroInesperado: "Algo deu errado. Tente de novo.",
    tentarDeNovo: "Tentar de novo",
    conectado: "conectado",
    reconectando: "reconectando...",
    precisaAtualizar:
      "O servidor fala uma versão diferente do protocolo. Atualize o jogo para voltar ao online.",
  },

  // -------------------------------------------------------------------------
  // Sessão de jogo solo
  // -------------------------------------------------------------------------
  //
  // O que a camada de jogo recusa, avisa ou enfileira. Mora aqui, e não no
  // motor, porque é regra do cliente: teto de elenco, caixa já comprometido na
  // fila, janela de transferências.
  //
  // O importante é onde **não** mora: `src/game/` roda dentro de um Web Worker,
  // que tem a própria cópia deste dicionário e nunca soube qual idioma a pessoa
  // escolheu. Qualquer frase escrita lá sai em português para todo mundo. Por
  // isso a sessão devolve chave e valores, e quem escreve é a tela.
  sessao: {
    // Ciclo de vida ---------------------------------------------------------
    semJogo: "Nenhum jogo em andamento.",
    semClubeEscolhido: "Nenhum clube escolhido.",
    semClubeValido: "Nenhum clube válido para assumir.",
    treinadorForaDaPartida: "Esse treinador não está nesta partida.",
    saveNaoEncontrado: "Save não encontrado.",
    semArmazenamento: "Este navegador não permite salvar partidas.",
    armazenamentoRecusou: "O navegador recusou o armazenamento do save.",
    naoSalvou: "Não foi possível salvar. O jogo continua, mas o progresso não está guardado.",
    encerrada: "A sessão de jogo foi encerrada. Recarregue a página.",
    motorParou: "O motor parou de responder.",
    falhaInesperada: "Algo deu errado no motor: {detalhe}",
    arquivoNaoServe: "Este arquivo não serve. {problemas}",

    // Depois da rodada ------------------------------------------------------
    temporadaEncerrada: "Temporada encerrada. Avance para a pré-temporada.",
    rodadaSemJogo: "Rodada {rodada} processada. Seu time não jogou nesta rodada.",

    // Partida ao vivo -------------------------------------------------------
    semPartidaAoVivo: "Não há partida em andamento.",
    semPartidaNaRodada: "Não há partida ao vivo nesta rodada.",
    trocaImpossivel: "Essa troca não é possível agora.",

    // Mercado ---------------------------------------------------------------
    janelaFechada: "A janela de transferências está fechada.",
    jogadorJaEhSeu: "Este jogador já é seu.",
    elencoNoLimite: "O elenco já está no limite de {maximo} jogadores.",
    naoDesceDePatamar: "{jogador} não aceitaria descer tanto de patamar.",
    salarioAbaixoDoPedido: "{jogador} pede pelo menos {salario} por rodada.",
    caixaNaoCobreEntrada:
      "O caixa não cobre a entrada desta proposta somada às que já estão na fila.",
    propostaMuitoBaixa: "Uma proposta tão baixa seria recusada na hora.",
    compraIndisponivel: "Não dá para comprar {jogador} na hora.",
    caixaNaoCobreCompra: "O caixa não cobre a compra somada às propostas que já estão na fila.",
    propostaFechada: "Esta proposta não está mais em aberto.",
    propostaDeOutroClube: "Esta proposta não é sua para responder.",

    // Olheiro ---------------------------------------------------------------
    olheiroNoProprioElenco: "Você não precisa de olheiro para o próprio elenco.",
    caixaNaoCobreOlheiro: "O caixa não cobre o custo do olheiro somado ao que já está na fila.",
    olheiroJaNaFila: "Esse olheiro já está na fila.",

    // Elenco ----------------------------------------------------------------
    jogadorDeOutroClube: "Este jogador não é seu.",
    acaoDesconhecida: "Ação desconhecida.",

    // Categorias de base ----------------------------------------------------
    garotoForaDaBase: "Esse garoto não está na sua base.",
    garotoEmFormacao: "{jogador} ainda está em formação. A decisão vem na idade dele.",
    garotoNoLimiteDeIdade: "{jogador} não pode passar dos {idade} anos na base.",

    // Clube -----------------------------------------------------------------
    ingressoForaDaFaixa: "O ingresso precisa ficar entre {minimo} e {maximo}.",
    bancoLiberaAte: "O banco libera no máximo {valor}.",
    caixaNaoCobrePagamento: "O caixa não cobre esse pagamento.",
    obraEmAndamento: "Termine a obra atual antes de começar outra.",
    estruturaDesconhecida: "Estrutura desconhecida.",
    obraUmNivelPorVez: "As estruturas sobem um nível de cada vez.",
    caixaNaoCobreObra: "O caixa não cobre a obra somada ao que já está na fila.",

    /*
     * A fila de decisões, uma linha por ordem dada.
     *
     * O estilo de jogo tem uma chave por estilo, e não um `{estilo}` dentro da
     * frase, pela mesma razão que o motor separa as contratações: quem traduz
     * precisa poder concordar a oração inteira, e "jogo defensivo" não é o
     * mesmo molde com uma palavra trocada em toda língua.
     */
    fila: {
      escalacaoDefensiva: "Escalação no {formacao}, jogo defensivo",
      escalacaoEquilibrada: "Escalação no {formacao}, jogo equilibrado",
      escalacaoOfensiva: "Escalação no {formacao}, jogo ofensivo",
      proposta: "Proposta por {jogador}",
      aceitarProposta: "Aceitar proposta por {jogador}",
      recusarProposta: "Recusar proposta por {jogador}",
      elogiar: "Elogiar {jogador}",
      criticar: "Dar bronca em {jogador}",
      renovar: "Renovar contrato de {jogador}",
      dispensar: "Dispensar {jogador}",
      listar: "Listar {jogador} para transferência",
      tirarDaLista: "Tirar {jogador} da lista",
      observar: "Observar {jogador}",
      emprestimo: "Pedir {jogador} emprestado, assumindo {parte}% do salário",
      promover: "Promover ao elenco: {jogador}",
      manter: "Segurar mais um ano na base: {jogador}",
      dispensarDaBase: "Dispensar da base: {jogador}",
      ingresso: "Ingresso a {valor}",
      emprestimoNoBanco: "Pedir empréstimo ao banco",
      abaterDivida: "Abater dívida",
      obra: "Iniciar obra",
      clube: "Ajuste no clube",
      jogadorDesconhecido: "jogador",
      garotoDesconhecido: "garoto da base",
    },
  },

  // -------------------------------------------------------------------------
  // Início
  // -------------------------------------------------------------------------
  home: {
    lema: "Crie sua história. Deixe seu legado.",
    continuarCom: "Continuar com o {clube}",
    novaCarreira: "Nova carreira",
    bancoProprio: "Começar com um banco de dados próprio",
    editorDeBanco: "Editor de banco de dados",
    erroDeArquivo: "Não consegui ler o arquivo. Ele precisa ser um JSON válido.",
    carreirasSalvas: "Carreiras salvas",
    resumoDoSave: "{treinador} · temporada {temporada}, rodada {rodada}/{total} · {quando}",
    apagar: "Apagar",
    modo: {
      umJogador: "1 Jogador",
      online: "Online Multiplayer",
      trocar: "Trocar de modo",
    },

    online: {
      procurando: "Procurando a sua carreira.",
      comecar: "Começar carreira",
      continuar: "Continuar com o {clube}",
      resumo: "{treinador} · temporada {temporada}, rodada {rodada} · {quando}",
      exportar: "Exportar para o modo 1 Jogador",
      apagar: "Apagar carreira",
      apagarConfirma: "Apagar a carreira desta conta? Não tem volta.",
    },


    versao: "alfa · versão {versao}",
  },

  // -------------------------------------------------------------------------
  // Nova carreira
  // -------------------------------------------------------------------------
  novoJogo: {
    gerandoRotulo: "Montando o mundo",
    gerandoTitulo: "Criando países, divisões, clubes e elencos",
    gerandoCorpo: "São mais de 8 mil jogadores com nome, contrato e história própria.",

    rotulo: "Nova carreira",
    titulo: "Escolha seu clube",
    resumoDaGeracao: "{clubes} clubes gerados em {ms} ms.",
    seuNome: "Seu nome",
    pais: "País",
    divisao: "Divisão",
    divisaoOpcao: "{numero}ª divisão",
    colunas: {
      clube: "Clube",
      reputacao: "Reputação",
      elenco: "Elenco",
      caixa: "Caixa",
    },
    tambemVaoJogar: "Também vão jogar",
    assento: "{treinador} · {clube}",
    tirar: "tirar",
    selecione: "Selecione um clube na lista.",
    clubeEscolhido: "{clube}, {liga}",
    adicionarTreinador: "Adicionar outro treinador",
    adicionarTreinadorDica: "Jogar com amigos no mesmo aparelho",
    comecarComTreinadores: "Começar com {total} treinadores",
    assumirOComando: "Assumir o comando",
  },

  // -------------------------------------------------------------------------
  // Criador de clube, dentro da nova carreira
  // -------------------------------------------------------------------------
  /*
   * Só rótulo, dado e confirmação.
   *
   * Cada linha daqui encosta no campo ou no botão que nomeia, e a prévia do
   * escudo fica na tela o tempo todo: quem está desenhando vê o efeito de cada
   * controle antes de qualquer frase descrever. Texto que explica a tela rouba
   * espaço da prévia, que é justamente o que precisa ser grande.
   *
   * As chaves de forma, de padrão e de categoria repetem o nome que o motor e
   * o catálogo usam, em inglês minúsculo. É de propósito: a tela recebe o valor
   * cru do dado e busca o rótulo por ele, sem tabela de tradução no meio.
   */
  criador: {
    titulo: "Novo clube",
    escudo: "Escudo",

    nome: {
      rotulo: "Nome do clube",
      /*
       * Termo ofensivo tem dois desfechos, e por isso dois textos. O óbvio
       * barra a criação na hora; o duvidoso deixa criar e marca para revisão.
       * Quem escreveu precisa saber em qual dos dois caiu, senão fica tentando
       * variações contra uma recusa que nem existe.
       */
      bloqueado: "Nome não permitido.",
      duvidoso: "Nome marcado para revisão.",
    },

    sigla: {
      rotulo: "Sigla",
      // A sigla é única dentro do mundo da carreira. A recusa vem com as livres
      // ao lado porque, sem elas, a pessoa tenta às cegas contra 320 clubes.
      jaExiste: "Sigla já usada neste mundo.",
      formato: "Três letras, de A a Z.",
      sugestoes: "Livres",
      bloqueado: "Sigla não permitida.",
      duvidoso: "Sigla marcada para revisão.",
    },

    forma: "Forma",
    formas: {
      shield: "Escudo",
      circle: "Círculo",
      diamond: "Losango",
      pennant: "Flâmula",
    },

    padrao: "Padrão",
    padroes: {
      solid: "Liso",
      sash: "Faixa",
      stripes: "Listras",
      halves: "Metades",
    },

    cores: {
      titulo: "Cores",
      fundo: "Fundo",
      segunda: "Segunda",
      contorno: "Contorno",
    },

    catalogo: {
      titulo: "Figuras",
      // Duas abas porque traço cheio e traço de contorno não combinam dentro do
      // mesmo escudo: separá-los na escolha evita a mistura antes dela nascer.
      abas: {
        cheios: "Cheios",
        contornos: "Contornos",
      },
      busca: "Buscar figura",
      nadaEncontrado: "Nada encontrado.",
      todas: "Todas",
      categorias: {
        animais: "Animais",
        armas: "Armas",
        esporte: "Esporte",
        heraldica: "Heráldica",
        maquina: "Máquina",
        natureza: "Natureza",
        simbolos: "Símbolos",
      },
    },

    peca: {
      titulo: "Peças",
      adicionar: "Adicionar",
      remover: "Remover",
      paraFrente: "Para frente",
      paraTras: "Para trás",
      girar: "Girar",
      tamanho: "Tamanho",
      cor: "Cor",
      contagem: "{total} de {maximo}",
      // O limite é de legibilidade, não de arquivo: com o escudo a 20 pixels na
      // tabela, a quinta peça já não se distingue das outras quatro.
      limite: "Máximo de {maximo} peças.",
      vazio: "Sem peças",
    },

    previa: {
      titulo: "Prévia",
      // Os três tamanhos em que o escudo aparece no jogo. A tabela é o caso
      // difícil e está aqui por isso: o que some em 20 pixels some para sempre,
      // e some depois de o escudo já estar fechado.
      tabela: "Tabela",
      /*
       * O tamanho do meio da fila tem nome de lugar, e não de porte. Entre a
       * tabela e o cabeçalho ele é o escudo do painel de jogo e do cartão da
       * próxima partida, que no jogo ficam entre 34 e 42 pixels. Chamá-lo de
       * "grande" poria essa palavra no menor de dois escudos vizinhos.
       */
      partida: "Partida",
      cabecalho: "Cabeçalho",
      // Sobra para a camisa, que não tem um terceiro lugar no jogo para nomear:
      // a prévia grande dela existe para conferir o desenho, não para caber em
      // algum canto da tela.
      grande: "Grande",
    },

    uniforme: {
      titulo: "Uniforme",
      padrao: "Padrão",
      padroes: {
        solid: "Liso",
        stripes: "Listras",
        sash: "Faixa",
        hoops: "Listras horizontais",
        halves: "Metades",
        checks: "Xadrez",
      },
      cores: "Cores",
      corPrincipal: "Principal",
      corSecundaria: "Secundária",
      peito: "Peça no peito",
      semPeca: "Sem peça",
    },

    confirmar: "Criar clube",
    // O escudo se escolhe uma vez e fica. O aviso mora junto do botão, e não
    // numa tela depois, porque é ali que ainda dá para voltar e mexer.
    escudoDefinitivo: "O escudo não muda depois.",

    creditos: {
      titulo: "Créditos de arte",
      catalogo: "Catálogo",
      autor: "Autor",
      licenca: "Licença",
      usadas: "Figuras usadas",
      figurasUsadas: "{total} figuras",
    },
  },

  // -------------------------------------------------------------------------
  // Hub do clube, incluindo a coluna de painéis da direita
  // -------------------------------------------------------------------------
  hub: {
    navAria: "Seções do jogo",
    sair: "Sair da carreira",
    nav: {
      elenco: "Elenco",
      tatica: "Tática",
      mercado: "Mercado",
      competicoes: "Competições",
      clube: "Clube",
      financas: "Caixa",
      noticias: "Notícias",
      carreira: "Carreira",
      base: "Base",
      /* O botão que abre a folha no celular. */
      mais: "Mais",
    },
    abrirDados: "Ver os dados do clube",
    fecharDados: "Esconder os dados do clube",
    fatos: {
      moral: "Moral",
      pontos: "Pontos",
      campanha: "V-E-D",
      treinador: "Treinador",
      estadio: "Estádio",
      fundacao: "Fundação",
      caixa: "Caixa",
      objetivo: "Objetivo",
    },
    lugares: "{capacidade} lugares",
    preTemporada: "Pré-temporada {temporada}",
    temporadaRodada: "Temporada {temporada} · rodada {rodada}/{total}",
    vezDe: "vez de",


    decisoesNaFila: "{total} decisão(ões) na fila",
    atalhosDica: "Atalhos de teclado",
    atalhosAria: "Ver os atalhos de teclado",
    simulando: "Simulando...",
    proximaTemporada: "Começar a próxima temporada",
    jogarRodada: "Jogar rodada {rodada}",
    avancarRodada: "Avançar rodada",
    filaTitulo: "Na fila para a próxima rodada ({total})",

    temporadaEncerrada: "Temporada encerrada.",
    semJogoMarcado: "Sem jogo marcado.",
    versus: "vs",
    emCasa: "Em casa",
    foraDeCasa: "Fora de casa",
    competicaoRodada: ", rodada {rodada}",
    competicaoFase: ", {fase}",
    classico: "Clássico",

    desempenho: "Desempenho na temporada",
    posicao: "Posição",
    posicaoValor: "{posicao}º",
    jogosDisputados: "{jogos} jogo(s) disputado(s)",
    vitorias: "Vitórias",
    empates: "Empates",
    derrotas: "Derrotas",
    saldo: "Saldo",
    ultimosJogos: "Últimos jogos",

    financas: "Caixa",
    saldoEmCaixa: "Saldo em caixa",
    receitaPorRodada: "Receita por rodada",
    despesaPorRodada: "Despesa por rodada",
    salarios: "Salários",
    moralExcelente: "Excelente",
    moralBoa: "Boa",
    moralRegular: "Regular",
    moralRuim: "Ruim",
    moralValor: "{rotulo} ({valor})",

    rivalidades: "Rivalidades",
  },

  // -------------------------------------------------------------------------
  // Elenco
  // -------------------------------------------------------------------------
  elenco: {
    titulo: "Elenco principal",
    colunas: {
      numero: "Nº",
      pos: "Pos",
      jogador: "Jogador",
      forca: "For",
      moral: "Moral",
      fisico: "Físico",
      gols: "Gols",
      situacao: "Situação",
      salario: "Salário",
    },
    lesionado: "lesionado {rodadas}",
    suspenso: "suspenso",
    linhaSecundaria: "{idade} anos · {destaques}",
    titular: "Titular",
    banco: "Banco",
    fora: "Fora",
  },

  // -------------------------------------------------------------------------
  // Categorias de base
  // -------------------------------------------------------------------------
  base: {
    titulo: "Categorias de base",
    turma: "{total} na base",
    explicacao:
      "Estes são os garotos em formação. O teto de cada um é invisível, como no resto do jogo: o que aparece é a faixa em que a comissão acredita. Aos 18 anos, cada um vira uma decisão sua: subir ao elenco, ficar mais um ano ou sair.",
    estrutura: "Centro de base nível {nivel}",
    oQueONivelCompra:
      "O nível da estrutura decide quantos garotos cabem aqui, quão bons chegam e quão estreita é a faixa de potencial.",
    vazia: "Nenhum garoto em formação. A próxima peneira acontece na virada da temporada.",

    decisaoTitulo: "Esperando a sua decisão",
    decisaoDica: "Entra na fila e vale quando o jogo avançar",
    promover: "Promover",
    manter: "Segurar mais um ano",
    dispensar: "Dispensar",
    prazo: "Pode ficar na base até a temporada {temporada}.",

    naBase: "Na base",
    esperandoTotal: "{total} esperando decisão",
    emFormacao: "Em formação",
    emFormacaoTotal: "{total} ainda sem idade de decidir",
    emFormacaoRotulo: "Em formação",
    esperaDecisao: "Espera decisão",
    parado: "Parou de evoluir",
    naFilaPromover: "Promover, na fila",
    naFilaManter: "Segurar, na fila",
    naFilaDispensar: "Dispensar, na fila",

    anos: "{idade} anos",
    faixa: "{baixo} a {alto}",
    potencialEstimado: "potencial estimado",
    evolucaoValor: "+{pontos}",

    colunas: {
      pos: "Pos",
      garoto: "Garoto",
      idade: "Idade",
      forca: "For",
      evolucao: "Evolução",
      faixa: "Potencial",
      situacao: "Situação",
    },

    veredito: {
      vaiCrescer: "Tem muito a crescer e vale a aposta.",
      quaseFeito: "Ainda evolui, mas já é quase o que vai ser.",
      noTeto: "Está no teto dele. O que você vê é o que você leva.",
      estagnou: "Passou o ano parado, e o teto dele encolheu junto.",
    },

    confianca: {
      alta: "confiança alta",
      media: "confiança média",
      baixa: "confiança baixa",
    },
  },

  // -------------------------------------------------------------------------
  // Tática
  // -------------------------------------------------------------------------
  tatica: {
    escalacao: "Escalação",
    forcaEmCampo: "força em campo {valor}",
    automatica: "Automática",
    formacao: "Formação",
    capitao: "Capitão",
    vagaAberta: "Vaga aberta",
    improvisado: "{pos} improvisado",
    naPosicao: "na posição",
    condicaoDoTitular: "· {condicao}%",

    bancoDeReservas: "Banco de reservas ({total})",
    nenhumReserva: "Nenhum reserva no banco.",
    foraDosRelacionados: "Fora dos relacionados",

    postura: "Postura em campo",
    estilo: "Estilo",
    estiloDefensivo: "Defensivo",
    estiloDefensivoAjuda: "Segura o resultado. Ataque menor, defesa maior.",
    estiloEquilibrado: "Equilibrado",
    estiloEquilibradoAjuda: "Sem inclinar para nenhum lado.",
    estiloOfensivo: "Ofensivo",
    estiloOfensivoAjuda: "Vai para cima. Cria mais e sofre mais.",
    marcacao: "Marcação",
    marcacaoLeve: "Leve",
    marcacaoLeveAjuda: "Menos faltas e menos cartão, defesa mais frouxa.",
    marcacaoNormal: "Normal",
    marcacaoNormalAjuda: "O padrão.",
    marcacaoForte: "Forte",
    marcacaoForteAjuda: "Defesa melhor, mas cansa o time e puxa cartão.",
    ritmo: "Ritmo",
    ritmoBaixo: "Baixo",
    ritmoBaixoAjuda: "Poupa o elenco ao custo de um pouco de rendimento.",
    ritmoNormal: "Normal",
    ritmoNormalAjuda: "O padrão.",
    ritmoAlto: "Alto",
    ritmoAltoAjuda: "Rende mais no primeiro tempo. Cai no segundo se faltar fôlego.",

    penaltis: "Pênaltis",
    faltas: "Faltas",
    escanteios: "Escanteios",
    automatico: "Automático",

    avisoDaFila: "A escalação entra na fila e vale a partir da próxima rodada.",
    confirmar: "Confirmar escalação",

    escolherPara: "Escolher jogador para {pos}",
    quemJogaAqui: "Quem joga aqui",
    idadeDoCandidato: "{idade} anos",
    fisicoDoCandidato: "{condicao}% físico",
    jaTitular: "já é titular, vai trocar de vaga",
    candidatoLesionado: "lesionado",
    candidatoSuspenso: "suspenso",

    forca: "Força",
    confirmarCurto: "Confirmar",
    confirmada: "Confirmada",
    reservas: "Reservas",
    abaBanco: "Banco",
    lugarVazio: "Lugar vazio",
    legenda: "Legenda",
    legCondicao: "Condição",
    legImprovisado: "Improvisado",
    legCansado: "Cansado",
    legPendurado: "Pendurado",
    legLesionado: "Lesionado",
    legSuspenso: "Suspenso",
    setorAta: "Ata",
    setorMei: "Mei",
    setorDef: "Def",
    naVaga: "Na vaga",
    condicaoCurta: "Cond.",
    moral: "Moral",
    media: "Média",
    golsAssist: "G / A",
    posicaoEIdade: "{pos} · {idade} anos",
    naturalEm: "{natural} em {vaga}",
    soltar: "Soltar",
    adversario: "Adversário",
    posicaoEPontos: "{posicao}º · {pontos} pts",
    dueloAtaque: "Ataque x Def",
    dueloMeio: "Meio x Meio",
    dueloDefesa: "Defesa x Ata",
    destaques: "Destaques",
    desfalques: "Desfalques",
    destaqueGols: "{valor} gols",
    destaqueAssist: "{valor} assist.",
    destaqueCapitao: "capitão",
    tituloLesionado: "Lesionado: {rodadas} rodadas",
    tituloSuspenso: "Suspenso: {rodadas} rodadas",
    tituloImprovisado: "Improvisado, natural {pos}",
    tituloCansado: "Condição {valor}%",
    tituloPendurado: "Pendurado: {amarelos} amarelos",
    rotuloDaFicha: "{nome}, {pos}, nota {nota}, condição {condicao}%",
  },

  // -------------------------------------------------------------------------
  // Mercado
  // -------------------------------------------------------------------------
  mercado: {
    janelaFechada:
      "A janela de transferências está fechada. Dá para olhar e montar a lista de alvos, mas não para contratar.",
    reabreNaRodada: "Reabre na rodada {rodada}.",
    reabreNaPreTemporada: "Reabre na pré-temporada.",

    propostasRecebidas: "Propostas recebidas ({total})",
    propostasEnviadas: "Propostas enviadas ({total})",

    procurarJogador: "Procurar jogador",
    encontrados: "{total} encontrados",
    nomeDoJogador: "Nome do jogador",
    todasAsPosicoes: "Todas as posições",
    qualquerIdade: "Qualquer idade",
    ateAnos: "até {idade} anos",
    qualquerForca: "Qualquer força",
    forcaOuMais: "força {valor} ou mais",
    soLivres: "Só livres",
    soOQueCabe: "Só o que cabe no caixa",

    colunas: {
      pos: "Pos",
      jogador: "Jogador",
      clube: "Clube",
      forca: "For",
      preco: "Preço",
      pede: "Pede",
    },
    naLista: "na lista",
    linhaSecundaria: "{idade} anos · {destaques}",
    livre: "livre",
    naoVem: "não vem",
    nenhumJogador: "Nenhum jogador com esses filtros.",

    jogadorResumo: "{pos}, força {forca}",
    ofertaRecebida: "Oferta do {clube} por {valor}",
    ofertaEnviada: "Proposta ao {clube} por {valor}",
    respondeEm: "responde em até {rodadas} rodada(s)",
    aceitaNaFila: "Aceita · na fila",
    recusadaNaFila: "Recusada · na fila",
    recusar: "Recusar",
    aceitar: "Aceitar",
    aguardando: "aguardando",
  },

  // -------------------------------------------------------------------------
  // Competições
  // -------------------------------------------------------------------------
  competicoes: {
    abaEstadual: "Estadual",
    abaCopa: "Copa",
    abaContinental: "Continental",
    abaArtilharia: "Artilharia",
    abaCalendario: "Calendário",

    campeao: "Campeão: {clube}",
    estadualResumo: "abre a temporada e junta as divisões do estado",

    calendario: {
      resumo: "{jogos} jogos na temporada, {classicos} deles clássicos",
      rodada: "Rodada {numero}",
      casa: "Em casa",
      fora: "Fora",
      neutro: "Campo neutro",
      proximo: "Próximo",
      vazio: "Nenhum jogo marcado para esta temporada.",
      todos: "Todos",
      filtroAjuda: "Filtre por torneio para achar o jogo que interessa.",
    },

    colunas: {
      posicao: "#",
      clube: "Clube",
      jogos: "J",
      vitorias: "V",
      empates: "E",
      derrotas: "D",
      golsPro: "GP",
      golsContra: "GC",
      saldo: "SG",
      pontos: "Pts",
    },

    seuGrupo: "seu grupo",
    tituloDaEdicao: "O título desta edição ficou com o {clube}.",
    doisPrimeiros:
      "Os dois primeiros de cada grupo avançam ao mata-mata. A faixa verde marca quem passa hoje.",
    gruposEncerrados: "Fase de grupos encerrada. O título sai no mata-mata.",
    mostrarSoMeuGrupo: "Mostrar só o meu grupo",
    verOutrosGrupos: "Ver os outros {total} grupos",
    esconderGrupos: "Esconder a fase de grupos",
    verGrupos: "Ver a fase de grupos",

    chaveamento: "Chaveamento",
    copaNaoComecou:
      "A copa ainda não começou. O sorteio da primeira fase sai quando a rodada chegar.",
    campanha: {
      naoClassificou: "Seu clube não se classificou para esta edição.",
      grupos: "Na fase de grupos.",
      gruposComMeu: "Na fase de grupos, no {grupo}.",
      eliminadoNosGrupos: "Eliminado na fase de grupos.",
      campeao: "Campeão da {competicao}.",
      tituloDeOutro: "Eliminado. O título ficou com o {clube}.",
      vivo: "Vivo na copa. Fase atual: {fase}.",
      vivoSemFase: "Vivo na copa.",
      eliminado: "Eliminado. Última fase: {fase}.",
      foraDaCopa: "Ainda não entrou na copa.",
    },
    idaEVolta: "ida e volta",
    jogoUnico: "jogo único",
    emDisputa: "em disputa",
    maisConfrontos: "e mais {total} confrontos",
    ida: "ida",
    aDefinir: "A definir",
    aDecidir: "a decidir",
    penaltis: "pênaltis",
    agregado: "agregado",

    artilharia: "Artilharia",
    ninguemMarcou: "Ninguém marcou ainda nesta temporada.",
    artilhariaDa: "Artilharia da {liga}",
    quantosNaLista: "{total} na lista",
    colunasArtilharia: {
      posicao: "#",
      pos: "Pos",
      jogador: "Jogador",
      clube: "Clube",
      jogos: "Jogos",
      assistencias: "Assist",
      gols: "Gols",
    },
  },

  // -------------------------------------------------------------------------
  // Clube
  // -------------------------------------------------------------------------
  clube: {
    precoDoIngresso: "Preço do ingresso",
    explicacaoDoPreco:
      "Cobrar caro demais esvazia o estádio e a renda cai junto. Os números abaixo são o público e a receita previstos contra um adversário mediano, pela mesma conta que o jogo usa na partida.",
    estadioLota:
      "O estádio lota no preço de hoje. Dá para cobrar até {preco} sem perder público, o que renderia {ganho} a mais por jogo em casa.",
    colunas: {
      ingresso: "Ingresso",
      publico: "Público previsto",
      ocupacao: "Ocupação",
      renda: "Renda por jogo",
    },
    atual: "atual",
    melhorRenda: "melhor renda",
    cobrar: "Cobrar",

    estruturas: "Estruturas",
    // O que cada estrutura muda. Estava cravado em `competition-views.ts`, e
    // aparecia em português debaixo de um nome de estrutura já traduzido.
    efeitoDaEstrutura: {
      stadium: "Capacidade e conforto do estádio. Mexe no público e na renda.",
      youth:
        "Quantos garotos cabem na base, quão bons eles chegam e quão estreita é a faixa de potencial que a comissão enxerga.",
      training: "Velocidade de evolução do elenco e recuperação física entre rodadas.",
      medical: "Reduz o tempo que um jogador fica fora depois de se machucar.",
    },
    obraEmAndamento: "Obra em andamento",
    obraDescricao: "{nome} subindo para o nível {nivel}. Faltam {rodadas} rodada(s).",
    manutencao: "manutenção {valor} por rodada",
    noMaximo: "no máximo",
    rodadasDeObra: "{rodadas} rodadas de obra",
    subirPara: "Subir para {nivel}",
    nivelAria: "nível {nivel} de 5",

    parcelas: "Parcelas de transferência",
    parcelasResumo: "{aPagar} a pagar · {aReceber} a receber",
    colunasParcelas: {
      jogador: "Jogador",
      fluxo: "Fluxo",
      venceEm: "Vence em",
      valor: "Valor",
    },
    aReceber: "a receber",
    aPagar: "a pagar",
    nestaRodada: "nesta rodada",
    emRodadas: "{rodadas} rodada(s)",

    banco: "Banco",
    caixa: "Caixa",
    divida: "Dívida",
    credito: "Crédito disponível",
    juros: "Juros por rodada",
    jurosValor: "{taxa}%",
    explicacaoDoBanco:
      "O banco cobra juros a cada rodada sobre o que você deve. Caixa negativo por muito tempo faz a diretoria vender jogador sem consultar ninguém.",
    descontoDeParcelas:
      "O crédito já desconta {valor} de parcelas de transferência a vencer.",
    pegarEmprestado: "Pegar emprestado",
    pedir: "Pedir",
    abaterDivida: "Abater dívida",
    pagar: "Pagar",
  },

  // -------------------------------------------------------------------------
  // Caixa
  // -------------------------------------------------------------------------
  financas: {
    titulo: "Caixa",
    saldoEmCaixa: "Saldo em caixa",
    receitaPorRodada: "Receita por rodada",
    despesaPorRodada: "Despesa por rodada",
    resultadoPorRodada: "Resultado por rodada",
    bilheteria: "Bilheteria na temporada",
    patrocinio: "Patrocínio na temporada",
    tv: "Direitos de TV na temporada",
    premiacao: "Premiação na temporada",
    folha: "Folha salarial por rodada",
    dividaComBanco: "Dívida com o banco",
    ingresso: "Ingresso",
    capacidade: "Capacidade do estádio",
  },

  // -------------------------------------------------------------------------
  // Notícias
  // -------------------------------------------------------------------------
  noticias: {
    titulo: "Notícias",
    nadaNovo: "Nada novo por aqui.",
    rodada: "rodada {rodada}",
    compartilharContratacao: "Compartilhar a contratação",
    contextoDoCartao: "Temporada {temporada} · rodada {rodada}",
    reforcoConfirmado: "Reforço confirmado",
  },

  // -------------------------------------------------------------------------
  // Carreira
  // -------------------------------------------------------------------------
  carreira: {
    /*
     * O que a carreira diz quando a conversa com o servidor falha.
     *
     * Nenhuma destas frases interrompe a partida: a rodada já aconteceu no
     * aparelho e o mundo inteiro sobe no próximo envio.
     */
    servidor: {
      falhaNoEnvio:
        "Não consegui guardar sua carreira no servidor agora. A rodada continua valendo e eu tento de novo.",
      semRede:
        "Não falei com o servidor.",
      downloadIncompleto:
        "A carreira chegou incompleta.",
      arquivoIlegivel:
        "Não consegui ler a carreira que veio do servidor.",
    },

    resumo: "{temporadas} temporada(s) · {titulos} título(s) · reputação {reputacao}",
    carreiraComeca: "A carreira começa agora. Termine a primeira temporada e ela aparece aqui.",
    colunas: {
      temporada: "Temp.",
      clube: "Clube",
      competicao: "Competição",
      posicao: "Posição",
      titulos: "Títulos",
    },
    posicaoValor: "{posicao}º",

    bancoDeDados: "Banco de dados",
    explicacaoDoBanco:
      'Salve os clubes e os elencos desta carreira num arquivo. Dá para corrigir um nome no bloco de notas, mandar para um amigo ou começar outra carreira com o mesmo banco. É o mesmo arquivo que a tela inicial lê em "começar com um banco de dados próprio".',
    salvarBanco: "Salvar banco de dados",
    salvamento: "Salvamento",
    salvarSozinho: "Salvar automaticamente a cada rodada",
    salvarAgora: "Salvar agora",
    salvo: "Carreira salva.",

    conquistas: "Conquistas",
    conquistasResumo: "{feitas} de {total}",
    temporadaDaConquista: "temp. {temporada}",
  },

  // -------------------------------------------------------------------------
  // Ficha do jogador
  // -------------------------------------------------------------------------
  ficha: {
    secaoCaracteristicas: "Características",
    abaProposta: "Proposta",
    abaEmprestimo: "Empréstimo",
    abaQuePesam: "Na posição ({total})",
    abaDemais: "Demais ({total})",
    temporada: {
      jogos: "Jogos",
      gols: "Gols",
      assistencias: "Assistências",
      cartoes: "Cartões",
    },
    aria: "Ficha de {nome}",
    subtitulo: "{clube} · {idade} anos · {pe} · {personalidade}",
    fatos: {
      valor: "Valor",
      salario: "Salário",
      contratoAte: "Contrato até",
      naTemporada: "Na temporada",
      naCarreira: "Na carreira",
    },
    contratoValor: "temporada {temporada}",
    jogosEGols: "{jogos} jogos, {gols} gols",
    moral: "Moral",
    fisico: "Físico",
    notaMedia: "Nota média",

    olheiro: "Olheiro",
    confianca: "confiança {valor}",
    laudoResumo: "Potencial estimado entre {minimo} e {maximo}. {veredito}",
    confiancaAlta: "confiança alta",
    confiancaMedia: "confiança média",
    confiancaBaixa: "confiança baixa",
    laudoAntigo: "laudo antigo",
    faixa: "{baixo} a {alto}",
    potencialEstimado: "potencial estimado",
    observarDeNovo: "Observar de novo por {valor}",
    semLaudo:
      "Você não sabe até onde ele pode chegar. Um olheiro devolve uma faixa de potencial, e quanto melhor o seu centro de base, mais estreita ela é.",
    mandarObservar: "Mandar observar por {valor}",

    emprestimo: "Emprestado pelo {dono} ao {tomador}. O {tomador} paga {parte}% do salário.",


    elogiar: "Elogiar",
    darBronca: "Dar bronca",
    renovarContrato: "Renovar contrato",
    tirarDaLista: "Tirar da lista",
    listarParaVenda: "Listar para venda",

    fazerProposta: "Fazer proposta",
    leilao: "{clubes} clubes disputam este jogador. O maior lance é {maiorLance}.",
    leilaoSeuLanceLidera: "O seu lance é o maior até agora.",
    leilaoPrecisaCobrir: "Para ser ouvido primeiro, é preciso cobrir.",
    naoDesceDePatamar: "{nome} não aceitaria descer tanto de patamar. Nem adianta tentar.",
    explicacaoDaProposta:
      "O clube começa a ouvir a partir de {minimo}. Ele pede pelo menos {salario} por rodada. Seu caixa é {caixa}.",
    valorDaCompra: "Valor da compra",
    salarioPorRodada: "Salário por rodada",
    anos: "Anos",
    parcelas: "Parcelas",
    aVista: "à vista",
    duasVezes: "2 vezes",
    tresVezes: "3 vezes",
    explicacaoDasParcelas:
      "Sai {entrada} agora e o resto em {restantes} parcela(s) de {entrada}, a cada 12 rodadas. O vendedor desconta o parcelamento na hora de decidir, então a proposta precisa ser mais alta para ter o mesmo peso.",
    enviarProposta: "Enviar proposta",
    comprarPor: "Comprar por {valor}",
    contratarAgora: "Contratar agora",
    janelaFechada: "Janela fechada",

    ouPegarEmprestado: "Ou pegar emprestado",
    explicacaoDoEmprestimo:
      "Ele joga por você até o fim da temporada e depois volta para o clube dele. Não custa luvas, mas você assume {parte}% do salário, ou seja {valor} por rodada.",
    pedirEmprestado: "Pedir emprestado",
  },

  // -------------------------------------------------------------------------
  // Resumo da partida
  // -------------------------------------------------------------------------
  partida: {
    aria: "Resultado da partida",
    vitoria: "Vitória",
    empate: "Empate",
    derrota: "Derrota",
    classico: "Clássico",
    competicaoFase: ", {fase}",
    posse: "Posse {valor}%",
    chances: "Chances {minhas} a {dele}",
    porQueAconteceu: "Por que aconteceu",
    lances: "Lances",
    minuto: "{minuto}'",
    compartilhar: "Compartilhar",
  },

  // -------------------------------------------------------------------------
  // Partida ao vivo
  // -------------------------------------------------------------------------
  aoVivo: {
    expandir: "Expandir a partida",
    encolher: "Encolher a partida",
    abaLances: "Lances",
    abaRodada: "Rodada",
    jogoAcabou: "encerrado",
    aria: "Partida ao vivo",
    fimDeJogo: "fim de jogo",
    minuto: "{minuto}'",
    bolaVaiRolar: "A bola vai rolar.",

    intervalo: "Intervalo",
    pausado: "Pausado",
    restamTrocas: "restam {trocas} trocas",
    semReservas: "Não há reservas disponíveis.",
    emCampo: "Em campo",
    banco: "Banco",
    ninguemEmCampo: "Ninguém em campo.",
    voltarParaOJogo: "Voltar para o jogo",

    pausar: "Pausar",
    ritmo1x: "1x",
    ritmo2x: "2x",
    ritmo4x: "4x",
    verResumo: "Ver o resumo da rodada",
    pularParaResultado: "Pular para o resultado",
    condicaoDoJogador: "{condicao}%",
  },

  // -------------------------------------------------------------------------
  // Fim de temporada
  // -------------------------------------------------------------------------
  fimDeTemporada: {
    contextoDoCartao: "Temporada {temporada}",
    encerrada: "Temporada {temporada} encerrada",
    posicaoValor: "{posicao}º",
    naLiga: "na {liga}",

    tituloConquistado: "Título conquistado",
    titulosConquistados: "Títulos conquistados",
    compartilharTitulo: "Compartilhar o título",

    // As conquistas de carreira destravadas nesta temporada. O motor já as
    // calculava e guardava no save, e elas só apareciam na tela de Carreira:
    // quem ganhava a primeira taça não ficava sabendo no balanço do ano.
    conquistaNova: "Conquista desbloqueada",
    conquistasNovas: "Conquistas desbloqueadas",
    verNaCarreira: "As outras estão na tela de Carreira.",

    // O parecer da diretoria. Saiu do save, onde era um parágrafo em português
    // congelado na redação do dia em que foi gravado, e passou a ser escrito
    // aqui a partir do veredito, da posição e dos títulos.
    veredito: {
      demitido: "A diretoria pedia: {objetivo}. O time terminou em {posicao}º e o contrato não foi renovado.",
      titulo: "Título é resposta que não precisa de explicação. A diretoria quer mais do mesmo.",
      acima: "A diretoria pedia: {objetivo}. Recebeu mais do que isso, e o crédito lá dentro aumentou.",
      cumpriu: "A diretoria pedia: {objetivo}. O {posicao}º lugar serve. Nem festa, nem cobrança.",
      abaixo: "A diretoria pedia: {objetivo}. O {posicao}º lugar ficou abaixo, e outra temporada assim muda o assunto.",
    },
    oQueADiretoriaAchou: "O que a diretoria achou",

    campanha: "Campanha",
    pontos: "Pontos",
    jogos: "Jogos",
    vitorias: "Vitórias",
    empates: "Empates",
    derrotas: "Derrotas",
    saldo: "Saldo",
    meta: "A meta era esta: {meta}, ou seja {posicao}º lugar.",

    caixaDaTemporada: "Caixa da temporada",
    receita: "Receita",
    despesa: "Despesa",
    resultado: "Resultado",
    caixaHoje: "Caixa hoje",
    divida: "Dívida",

    destaques: "Destaques",
    artilheiro: "Artilheiro",
    artilheiroValor: "{nome}, {gols} gols",
    ninguemDestaque: "Ninguém marcou o suficiente para virar destaque.",
    melhorNota: "Melhor nota",
    melhorNotaValor: "{nome}, {nota}",

    subiramDaBase: "Subiram da base",
    garotos: "{total} garotos",
    contratosAcabando: "Contratos acabando",
    renoveAntes: "renove antes que saiam de graça",
    renovar: "Renovar",
    colunas: {
      pos: "Pos",
      jogador: "Jogador",
      idade: "Idade",
      forca: "Força",
      salario: "Salário",
    },

    quemChamou: "Quem chamou você",
    sondagens: "Sondagens de outros clubes",
    escolhaOndeRecomecar: "escolha onde recomeçar",
    aceitarTroca: "aceitar troca seu clube agora",
    ofertaLinha: "{liga} · {pais} · pedem: {objetivo}",
    caixaDe: "caixa de {valor}",
    assumir: "Assumir",
    verClube: "Ver clube",
    previaCaixa: "Caixa",
    previaDivida: "Dívida",
    previaFolha: "Folha por rodada",
    previaEstadio: "Estádio",
    previaPosicao: "Posição",
    previaForca: "Força do elenco",
    previaIdade: "Idade média",
    previaElenco: "Elenco ({total})",
    previaAnos: "{idade} anos",

    semClube: "Sem clube",
    ninguemChamou: "Ninguém chamou desta vez. Comece uma carreira nova quando quiser.",
    escolhaUmClube: "Escolha um clube para seguir",
    comecarTemporada: "Começar a temporada {temporada}",
  },

  // -------------------------------------------------------------------------
  // Conta
  // -------------------------------------------------------------------------
  conta: {
    chaveDoAlfa: "Chave de acesso alfa",
    chaveDoAlfaAjuda: "No formato LFM-XXXX-XXXX.",
    erroEmail: "E-mail inválido.",
    erroCodigo: "Código inválido.",
    erroSenhaVazada: "Essa senha já apareceu em vazamento público. Escolha outra.",
    confiraCampos: "Confira: {campos}.",
    titulo: "Conta",
    voltar: "Voltar ao jogo",

    /** Nome do aparelho na lista de dispositivos ativos. */
    aparelhos: {
      web: "Navegador",
      android: "Android",
      ios: "iPhone ou iPad",
      desktop: "Computador",
    },

    email: "E-mail",
    senha: "Senha",
    novaSenha: "Nova senha",
    nome: "Nome de exibição",
    codigo: "Código que chegou por e-mail",
    senhaAjuda: "Pelo menos {minimo} caracteres.",
    nomeAjuda: "De {minimo} a {maximo} caracteres. É assim que você aparece nas ligas.",

    entrar: "Entrar",
    entrando: "Entrando...",
    criarConta: "Criar conta",
    criando: "Criando...",
    naoTenhoConta: "Ainda não tenho conta",
    jaTenhoConta: "Já tenho conta",
    esqueciASenha: "Esqueci minha senha",
    enviarLink: "Enviar o link",
    enviando: "Enviando...",
    redefinir: "Redefinir a senha",
    jaTenhoCodigo: "Já tenho o código",
    voltarParaEntrar: "Voltar para entrar",

    aceite:
      "Ao criar a conta você aceita os termos de uso e a política de privacidade, na versão de {versao}.",
    contaCriada:
      "Conta criada. Mandamos um e-mail para {email}. Confirme o endereço e depois entre.",
    linkEnviado: "Se existir conta com esse e-mail, o link de recuperação já está a caminho.",
    senhaTrocada: "Senha trocada. Entre com a senha nova.",

    sairDaConta: "Sair da conta",
    emailNaoConfirmado:
      "E-mail ainda não confirmado. Sem isso, o modo online e as compras ficam bloqueados.",
    reenviarConfirmacao: "Reenviar a confirmação",
    confirmacaoReenviada: "Mandamos outro link. Confira a caixa de entrada.",
    contaDesde: "Na casa desde {data}",

    perfil: "Perfil",
    salvar: "Salvar",
    salvando: "Salvando...",
    perfilSalvo: "Perfil salvo.",

    dispositivos: "Dispositivos ativos",
    dispositivosAjuda:
      "Cada aparelho que entrou nesta conta aparece aqui. Não reconheceu algum? Derrube e troque a senha.",
    esteAparelho: "este aparelho",
    ultimoUso: "último uso {quando}",
    entrouEm: "entrou {quando}",
    derrubar: "Derrubar",
    semDispositivos: "Nenhum dispositivo ativo.",

    direitos: "Direitos e assinatura",
    vitalicia: "Licença Vitalícia Solo",
    passe: "Passe Legacy",
    temDireito: "Ativo",
    semDireito: "Não tem",
    renovaEm: "renova em {data}",
    semRenovacao: "sem renovação automática",
    porOrigem: "por {origem}",
    origens: {
      stripe: "cartão",
      pix: "Pix",
      play: "Google Play",
      appstore: "App Store",
      grant: "concessão",
    },
    cosmeticos: "Cosméticos",
    cosmeticosTotal: "{total} item(ns)",

    dados: "Seus dados",

    excluirTitulo: "Excluir a conta",
    excluirComoFunciona:
      "A conta é marcada para exclusão e você perde o acesso ao online na hora. Por 7 dias dá para cancelar entrando de novo. No sétimo dia apagamos o que for possível apagar.",
    excluirOQueSai:
      "O que sai: e-mail, nome, senha, avatar, sessões, tokens de notificação, a carreira, datapacks privados, amizades e mensagens diretas.",
    excluirOQueFica: "O que fica, e por quê",
    excluirFicaFiscal: "Registro fiscal de compra: obrigação legal de 5 anos.",
    excluirFicaLigas:
      "Resultados e tabelas das ligas que você disputou: são de todos os participantes, e o seu nome vira \"Treinador removido\".",
    excluirFicaDatapacks:
      "Datapacks públicos que você publicou: outras pessoas dependem deles, e a autoria vira anônima. Dá para despublicar antes.",
    excluirFicaSancao: "Registro de sanção: enquanto durar, para a punição não acabar com um clique.",
    excluir: "Excluir minha conta",
    excluirConfirmar: "Escreva {palavra} abaixo para confirmar.",
    excluirPalavra: "EXCLUIR",
    excluirAgora: "Confirmar a exclusão",
    exclusaoAgendada: "Exclusão marcada para {data}. Até lá, dá para cancelar aqui mesmo.",
    cancelarExclusao: "Cancelar a exclusão",
    exclusaoCancelada: "Exclusão cancelada. Sua conta continua no lugar.",
  },

  // -------------------------------------------------------------------------
  // Avisos no aparelho
  // -------------------------------------------------------------------------
  /*
   * Fica em bloco próprio porque aparece em duas telas: o convite no saguão da
   * liga e as preferências na conta. Repetir as mesmas frases nos dois lugares
   * criaria duas redações para a mesma coisa, e um dia elas discordariam.
   */
  admin: {
    titulo: "Painel",
    voltar: "Voltar ao jogo",
    semPermissao: "Esta área é só para administradores.",
    precisaEntrar: "Entre na sua conta para abrir o painel.",
    menu: {
      chaves: "Chaves alfa",
      bandeiras: "Bandeiras",
    },
    chaves: {
      titulo: "Chaves de acesso alfa",
      total: "Geradas",
      livres: "Livres",
      usadas: "Usadas",
      quantidade: "Quantas gerar",
      nota: "Anotação",
      notaAjuda: "Para lembrar a quem o lote se destinava. Opcional.",
      gerar: "Gerar",
      gerando: "Gerando...",
      copiar: "Copiar",
      copiada: "Copiada",
      revogar: "Revogar",
      chave: "Chave",
      estado: "Estado",
      quemUsou: "Quem usou",
      livre: "Livre",
      usadaPor: "Usada por {nome}",
      usadaPorAlguem: "Usada",
      vazio: "Nenhuma chave ainda. Gere a primeira.",
    },
    bandeiras: {
      titulo: "Bandeiras de funcionalidade",
      alfaFechado: "Alfa fechado",
      alfaFechadoAjuda: "Ligada, criar conta exige chave de acesso.",
      ligada: "Ligada",
      desligada: "Desligada",
      vazio: "Nenhuma bandeira configurada.",
    },
  },
  notificacoes: {
    titulo: "Avisos",
    ajuda:
      "Escolha o que vale um aviso no aparelho. O que você desligar aqui continua aparecendo na sua lista dentro do jogo.",

    /* Nome de cada tipo. As chaves são as mesmas que a API guarda. */
    tipos: {
      deadline: "Prazo da rodada chegando",
      roundProcessed: "Rodada processada",
      offers: "Propostas e leilões",
      chat: "Menções no chat da liga",
      marketing: "Novidades do Legacy Manager",
    },
    tiposAjuda: {
      deadline: "Um lembrete antes de a rodada fechar, para dar tempo de escalar.",
      roundProcessed: "O resultado assim que a rodada roda.",
      offers: "Quando alguém propõe pelo seu jogador ou cobre o seu lance.",
      chat: "Só quando escrevem o seu nome.",
      marketing: "Novidades e temporadas novas. Nunca propaganda de terceiros.",
    },

    salvando: "Salvando...",
    salvo: "Preferências salvas.",

    /* O convite, no saguão da liga assíncrona. */
    convite: {
      titulo: "Quer ser avisado quando a rodada rodar?",
      corpo:
        "Esta liga roda sozinha, no horário combinado. Sem aviso, dá para perder a escalação e só descobrir o resultado no dia seguinte.",
      sim: "Quero ser avisado",
      agoraNao: "Agora não",
      pedindo: "Aguardando o navegador...",
    },

    /* O que aparece depois da resposta do navegador. */
    ativados: "Pronto. Você vai receber o aviso da rodada neste aparelho.",
    negados:
      "Sem problema. Dá para ligar depois nas configurações de notificação do navegador.",
    indisponiveis: "Este aparelho não recebe avisos do navegador.",
    semServidor: "Este servidor está sem avisos no aparelho configurados.",
    falhou: "Não consegui ligar os avisos agora. Dá para tentar de novo mais tarde.",
    jaAtivos: "Os avisos deste aparelho estão ligados.",
    bloqueados:
      "Os avisos estão bloqueados para o jogo neste navegador. Para voltar, libere nas configurações do site.",
  },

  // -------------------------------------------------------------------------
  // Ligas online
  // -------------------------------------------------------------------------
  opcoes: {
    som: {
      titulo: "Som",
      gol: "Som de gol na partida ao vivo",
    },
    titulo: "Opções",
    voltar: "Voltar",

    tema: {
      titulo: "Tema",
      escuro: "Escuro",
      claro: "Claro",
      brasao: "Brasão",
      /* Só aparece quando não há clube de onde tirar cor. É dado, não conselho. */
      semClube: "Disponível com uma carreira aberta.",
    },
    idioma: {
      titulo: "Idioma",
      ajuda:
        "Vale para este aparelho na hora. Se você tiver conta, a escolha sobe para ela e o próximo aparelho já abre assim.",
    },

    avisos: {
      precisaDeConta:
        "Os avisos são guardados na sua conta, uma escolha por pessoa. Entre na sua conta para escolher quais você quer receber.",
    },


    erros: {
      titulo: "Relatório de erros",
      enviar: "Mandar um relatório quando o jogo quebrar",
      oQueVai:
        "Vai o que quebrou, a versão do jogo, a plataforma, a semente do mundo e o número da rodada.",
      saveNuncaVai:
        "O seu save nunca é enviado. Semente e rodada bastam para reproduzir o problema, porque a simulação é determinística.",
      semEndereco:
        "Esta versão do jogo foi montada sem destino para relatórios, então não há o que enviar de qualquer forma.",
    },


    sobre: {
      titulo: "Sobre",
      versaoDoJogo: "Versão do jogo",
      versaoDoMotor: "Versão do motor",
      versaoDaApi: "Versão da API",
      protocolo: "Protocolo",
      motorDoServidor: "O servidor está rodando o motor {versao}.",
      licencas: "Licenças",
      licencaDoJogo:
        "O Legacy Manager é distribuído sob licença de uso pessoal. Estas são as bibliotecas de código aberto que ele carrega junto.",
    },
  },

  ligas: {
    titulo: "Ligas online",
    voltar: "Voltar",
    atualizar: "Atualizar",
    carregarMais: "Carregar mais",

    abas: {
      minhas: "Minhas ligas",
      publicas: "Ligas públicas",
      criar: "Criar liga",
    },

    semMinhas: "Você ainda não está em nenhuma liga. Entre por convite ou crie a sua.",
    semPublicas: "Nenhuma liga pública aberta agora.",

    entrarPorCodigo: "Entrar por código de convite",
    codigo: "Código",
    codigoAjuda: "De 6 a 12 letras e números, como o anfitrião passou.",
    entrar: "Entrar",
    entrou: "Pronto, você está na {liga}.",

    estados: {
      lobby: "No saguão",
      running: "Em andamento",
      paused: "Pausada",
      finished: "Encerrada",
      stalled: "Travada",
    },
    visibilidades: {
      private: "Privada",
      public: "Pública",
    },

    linha: {
      temporadaRodada: "Temporada {temporada} · rodada {rodada}",
      treinadores: "{humanos} de {max} treinadores",
      prazo: "Prazo em {quando}",
      semPrazo: "Sem rodada marcada",
      meuClube: "Seu clube: {clube}",
      abrir: "Abrir",
    },

    criar: {
      titulo: "Criar liga",
      exigePasse: "Abrir liga faz parte do Passe Legacy.",
      nome: "Nome da liga",
      visibilidade: "Quem pode entrar",
      privada: "Só quem tiver o código",
      publica: "Qualquer pessoa, pela vitrine",
      maxHumanos: "Máximo de treinadores",
      paises: "Países",
      paisesAjuda: "De 1 a 8 países. Cada um entra com as divisões escolhidas abaixo.",
      paisesNomes: {
        BR: "Brasil",
        AR: "Argentina",
        ES: "Espanha",
        EN: "Inglaterra",
        IT: "Itália",
        PT: "Portugal",
      },
      divisoes: "Divisões por país",
      divisoesOpcao: "{numero} divisão(ões)",
      ritmo: "Ritmo",
      ritmoAsync: "Por prazo",
      ritmoAsyncAjuda:
        "A rodada roda na hora marcada. Quem não mandou decisão joga com a escalação anterior.",
      ritmoLive: "Ao vivo",
      ritmoLiveAjuda: "A rodada roda quando todo mundo marca pronto, respeitando a espera máxima.",
      cron: "Quando a rodada roda",
      cronAjuda: "Expressão cron de cinco campos, no fuso escolhido.",
      fuso: "Fuso horário",
      esperaAoVivo: "Espera pelos ausentes, em minutos",
      temporadas: "Temporadas",
      distribuicao: "Como os clubes são distribuídos",
      distribuicaoSorteio: "Sorteio",
      distribuicaoDraft: "Draft",
      dificuldade: "Dificuldade",
      dificuldades: {
        facil: "Fácil",
        normal: "Normal",
        dificil: "Difícil",
      },
      mercado: "Mercado entre humanos",
      mercadoLigado: "Permitir negócio entre treinadores humanos",
      razaoMinima: "Proposta mínima, em relação ao valor de mercado",
      razaoMinimaValor: "{valor}%",
      negociosPorDupla: "Negócios por dupla de clubes, por temporada",
      aprovacao: "O comissário aprova cada negócio",
      criar: "Criar a liga",
      criando: "Criando...",
      escolhaUmPais: "Escolha pelo menos um país.",
    },

    saguao: {
      membros: "Treinadores ({total} de {max})",
      comissario: "comissário",
      semClube: "clube a definir",
      online: "on-line",
      offline: "fora do ar",
      pronto: "decisão enviada",
      semDecisao: "sem decisão nesta rodada",
      ultimaAcao: "última ação {quando}",
      situacoes: {
        active: "ativo",
        left: "saiu",
        kicked: "expulso",
      },
      prazo: "Prazo",
      convite: "Código de convite",
      copiar: "Copiar",
      copiado: "Código copiado.",
      gerarConvite: "Gerar outro código",
      conviteExpira: "Vale até {data}",
      comecar: "Começar a liga",
      comecando: "Começando...",
      comecarAjuda:
        "Só o comissário começa. A partir daí ninguém mais entra e os clubes são distribuídos.",
      sair: "Sair da liga",
      expulsar: "Expulsar",
      pausar: "Pausar",
      retomar: "Retomar",
      forcarRodada: "Rodar a rodada agora",
      rodadaPedida: "Rodada {rodada} pedida. O servidor processa em instantes.",
      abrirHub: "Entrar na liga",
      configuracao: "Configuração",
      resumo:
        "{paises} · {divisoes} divisão(ões) por país · {temporadas} temporada(s) · dificuldade {dificuldade}",
      mercadoLigado: "Mercado entre humanos ligado, mínimo de {razao}% do valor.",
      mercadoDesligado: "Sem negócio entre treinadores humanos.",
    },
  },

  // -------------------------------------------------------------------------
  // Hub da liga online
  // -------------------------------------------------------------------------
  hubOnline: {
    assistir: "Assistir à partida",
    proximoJogo: "Próximo jogo",
    navAria: "Seções da liga",
    sair: "Sair da liga",
    nav: {
      elenco: "Elenco",
      tabela: "Tabela",
      noticias: "Notícias",
      treinadores: "Treinadores",
      chat: "Chat",
    },

    prazoRotulo: "Prazo da rodada",
    semPrazo: "Sem rodada marcada",
    prazoVencido: "Prazo vencido. A rodada roda a qualquer momento.",
    processando: "Rodada {rodada} sendo processada. As decisões estão travadas.",
    pausada: "Liga pausada pelo comissário.",
    encerrada: "Liga encerrada.",
    travada: "A liga travou e precisa do comissário.",
    avisoDePrazo: "Faltam {minutos} minuto(s) para o prazo da rodada {rodada}.",

    temporadaRodada: "Temporada {temporada} · rodada {rodada}/{total}",
    caixa: "Caixa",
    divida: "Dívida",
    estadio: "Estádio",
    lugares: "{capacidade} lugares",
    objetivo: "Objetivo",

    semJogoMarcado: "Sem jogo marcado nesta rodada.",
    emCasa: "Em casa",
    foraDeCasa: "Fora de casa",
    versus: "vs",

    decisoes: "Decisões da rodada",
    prontos: "{prontos} de {total} prontos",
    estouPronto: "Marcar decisão como enviada",
    aindaNao: "Desmarcar",
    decisoesAjuda:
      "No ritmo ao vivo, a rodada roda assim que todo mundo marcar. No ritmo por prazo, a marca serve para os outros saberem que você já resolveu a sua parte.",

    elenco: "Elenco",
    elencoColunas: {
      numero: "Nº",
      pos: "Pos",
      jogador: "Jogador",
      forca: "For",
      moral: "Moral",
      fisico: "Físico",
      salario: "Salário",
    },
    lesionado: "lesionado {rodadas}",
    suspenso: "suspenso",
    linhaSecundaria: "{idade} anos · {destaques}",

    tabela: "Tabela",
    semTabela: "A tabela aparece quando a primeira rodada rolar.",
    noticias: "Notícias",
    semNoticias: "Nada novo por aqui.",
    noticiaRodada: "rodada {rodada}",
    treinadores: "Treinadores",

    chat: "Chat da liga",
    chatVazio: "Ninguém falou nada ainda.",
    chatCampo: "Escreva uma mensagem",
    chatEnviar: "Enviar",
    chatDigitando: "alguém está digitando...",
    chatSemHistorico:
      "O histórico não está disponível neste servidor. As mensagens novas aparecem aqui.",
    chatSistema: "Aviso da liga",
    chatDesconectado: "Sem conexão com a liga. A mensagem só sai quando ela voltar.",

    resultado: "Rodada {rodada}: {casa} {golsCasa} x {golsFora} {fora}",
    posicaoAgora: "Você está em {posicao}º.",
    atualizar: "Atualizar",
    semClube: "Você ainda não tem clube nesta liga. Por enquanto, só dá para acompanhar.",
  },

  // -------------------------------------------------------------------------
  // Partida ao vivo pelo servidor
  // -------------------------------------------------------------------------
  partidaOnline: {
    aria: "Partida ao vivo da liga",
    conectando: "Entrando no canal da partida...",
    esperando: "A bola vai rolar.",
    minuto: "{minuto}'",
    fimDeJogo: "fim de jogo",
    desconectado: "Conexão caída. O jogo volta quando ela voltar.",
    fechar: "Fechar",

    pausa: "Pausa para substituição",
    janela: "janela {janela} de 3",
    tempoParaDecidir: "{segundos}s para decidir",
    semReservas: "Não há reservas no banco.",
    emCampo: "Em campo",
    banco: "Banco",
    ninguemEmCampo: "Ninguém em campo.",
    trocaEnviada: "Troca enviada. Ela vale no resultado oficial da rodada.",
    seguirSemMexer: "Seguir sem mexer",
    semClubeNaPartida: "Você está assistindo como espectador, então não dá para substituir.",

    posse: "Posse",
    chances: "Chances",
    finalizacoes: "Finalizações",
    cartoes: "Cartões",
    porQueAconteceu: "Por que aconteceu",
  },

  // -------------------------------------------------------------------------
  // Ajuda dos atalhos de teclado
  // -------------------------------------------------------------------------
  // -------------------------------------------------------------------------
  // Editor de banco de dados
  // -------------------------------------------------------------------------
  editor: {
    titulo: "Editor de banco",

    acoes: {
      abrir: "Abrir arquivo",
      gerar: "Gerar banco",
      branco: "Começar em branco",
      salvar: "Salvar arquivo",
      jogar: "Jogar a rodada e acompanhar a partida",
      sair: "Sair do editor",
    },

    estado: {
      gerando: "Montando um banco novo. Leva alguns segundos.",
      semBancoTitulo: "Nenhum banco aberto",
      semBancoCorpo:
        "Abra um arquivo que você já tem, gere um banco completo com os 320 clubes ou comece em branco e monte o seu do zero.",
      aberto: "Aberto em {ms} ms",
      /** Contagens do cabeçalho, cada uma com singular e plural. */
      contagem: {
        paisUm: "1 país",
        paisMuitos: "{total} países",
        divisaoUm: "1 divisão",
        divisaoMuitos: "{total} divisões",
        clubeUm: "1 clube",
        clubeMuitos: "{total} clubes",
        jogadorUm: "1 jogador",
        jogadorMuitos: "{total} jogadores",
      },
      salvo: "Arquivo salvo como {nome}",
    },

    erro: {
      arquivo: "Não deu para ler este arquivo. Ele precisa ser um JSON de banco de dados.",
      semBanco: "Abra ou gere um banco antes.",
      invalido:
        "O banco ainda tem {total} problemas. Resolva antes de começar a carreira, senão o jogo recusa o arquivo.",
    },

    abas: {
      estrutura: "Estrutura",
      jogadores: "Jogadores",
      massa: "Em massa",
    },

    arquivo: {
      titulo: "Arquivo",
      nome: "Nome do banco",
      autor: "Autor",
      notas: "Observações",
      notasAjuda: "Some aqui a fonte dos dados e o que mudou nesta versão.",
    },

    arvore: {
      titulo: "Países e divisões",
      novoPais: "Novo país",
      novaDivisao: "Nova divisão",
      novoClube: "Novo clube",
      clubeUm: "1 clube",
      clubes: "{total} clubes",
    },
    /** Nome que um item novo recebe antes de alguém batizá-lo. Vira conteúdo do
     * arquivo, então nasce na língua de quem está editando. */
    padroes: {
      banco: "Banco novo",
      pais: "País novo",
      divisao: "Divisão",
      clube: "Clube",
      estado: "Estado",
      copa: "Copa",
    },

    pais: {
      titulo: "País",
      nome: "Nome",
      sigla: "Sigla",
      siglaAjuda: "Até quatro letras e única no arquivo. É ela que liga jogador e país.",
      adjetivo: "Adjetivo",
      adjetivoAjuda: "Como em Brasileiro. Aparece nos textos de notícia e de seleção.",
      forca: "Força do país",
      forcaAjuda: "De 1 a 5. Mede o peso do futebol do país: premiação, torcida e cotação.",
      copa: "Nome da copa",
      estados: "Estados",
      estadosAjuda: "Um por linha. Clube sem estado fica de fora do estadual.",
      divisoes: "Divisões",
      remover: "Remover país",
    },

    divisao: {
      titulo: "Divisão",
      nome: "Nome",
      nivel: "Nível",
      nivelAjuda: "1 é a elite. O nível ordena o acesso entre as divisões do país.",
      acesso: "Sobem",
      acessoAjuda: "Quantos clubes sobem para a divisão de cima no fim da temporada.",
      queda: "Caem",
      quedaAjuda: "Quantos clubes caem para a divisão de baixo.",
      clubes: "Clubes da divisão",
      remover: "Remover divisão",
    },

    clube: {
      titulo: "Clube",
      nome: "Nome",
      sigla: "Sigla",
      siglaAjuda: "Três letras, como aparece no escudo e na tabela.",
      fundacao: "Fundação",
      cores: "Cores",
      corPrincipal: "Cor principal",
      corSecundaria: "Cor secundária",
      reputacao: "Reputação",
      reputacaoAjuda: "De 1 a 6. Puxa patrocínio, torcida e quem aceita jogar no clube.",
      torcida: "Torcida",
      capacidade: "Capacidade do estádio",
      ingresso: "Preço do ingresso",
      caixa: "Caixa",
      estado: "Joga o estadual",
      semEstado: "Sem estadual",
      estruturas: "Estruturas",
      elenco: "Elenco",
      elencoResumo: "{jogadores} · {goleiros} · força {forca}",
      goleiroUm: "1 goleiro",
      goleiroMuitos: "{total} goleiros",
      semGoleiro: "nenhum goleiro",
      novoJogador: "Novo jogador",
      duplicar: "Duplicar clube",
      mover: "Mover de divisão",
      moverAjuda: "Leva o clube com o elenco inteiro para outra divisão do mesmo país.",
      remover: "Remover clube",
    },

    estruturas: {
      stadium: "Estádio",
      youth: "Base",
      training: "Treinamento",
      medical: "Departamento médico",
    },

    reputacoes: {
      um: "1 Municipal",
      dois: "2 Estadual",
      tres: "3 Regional",
      quatro: "4 Nacional",
      cinco: "5 Continental",
      seis: "6 Mundial",
    },

    jogador: {
      titulo: "Jogador",
      nome: "Nome",
      nacionalidade: "Nacionalidade",
      nacionalidadeAjuda: "A sigla do país, como BR. É o que define a seleção dele.",
      posicao: "Posição",
      idade: "Idade",
      pe: "Pé",
      atributos: "Atributos",
      atributosAjuda:
        "Os quatorze do motor. A porcentagem ao lado do nome é o peso dele na posição atual.",
      potencial: "Potencial",
      potencialAjuda: "Teto de força. Nunca fica abaixo da força de hoje.",
      personalidade: "Personalidade",
      lesao: "Propensão a lesão",
      salario: "Salário por rodada",
      contrato: "Anos de contrato",
      forca: "Força",
      forcaDerivada:
        "A força não se edita. Ela sai dos atributos que a posição pesa, pela mesma conta que o motor faz, e muda sozinha quando você mexe neles.",
      voltar: "Voltar ao clube",
      remover: "Remover jogador",
    },

    pes: {
      D: "Direito",
      E: "Esquerdo",
      A: "Ambos",
    },

    personalidades: {
      lider: "Líder",
      profissional: "Profissional",
      ambicioso: "Ambicioso",
      temperamental: "Temperamental",
      mercenario: "Mercenário",
      tranquilo: "Tranquilo",
    },

    lesoes: {
      baixa: "Baixa",
      media: "Média",
      alta: "Alta",
    },

    jogadores: {
      titulo: "Jogadores do banco",
      procurar: "Procurar por nome",
      todasAsPosicoes: "Todas as posições",
      todosOsPaises: "Todos os países",
      encontrados: "{total} de {geral}",
      vazio: "Nenhum jogador com esse filtro.",
      colunas: {
        nome: "Nome",
        pos: "Pos",
        idade: "Idade",
        forca: "Força",
        clube: "Clube",
      },
    },

    massa: {
      titulo: "Operações em massa",
      renomear: "Renomear em lote",
      procurar: "Procurar",
      substituir: "Substituir por",
      alvo: "Onde trocar",
      escopo: "Em que país",
      todosOsPaises: "Todos os países",
      previa: "{total} nomes seriam trocados",
      previaUm: "1 nome seria trocado",
      previaNenhum: "Nenhum nome com esse texto",
      aplicar: "Trocar tudo",
      aviso:
        "A busca diferencia maiúscula de minúscula e troca todas as aparições dentro do nome.",
      feito: "{total} nomes trocados.",
      feitoUm: "1 nome trocado.",
      alvos: {
        clubes: "Nomes de clubes",
        jogadores: "Nomes de jogadores",
        ambos: "Clubes e jogadores",
      },
      atalhos: "Duplicar e mover clube ficam na tela do clube, junto do resto da edição.",
    },

    problemas: {
      titulo: "Problemas",
      nenhum: "Nenhum problema. Este banco abre carreira.",
      contagem: "{total} problemas",
      um: "1 problema",
      ajuda: "Clique em um problema para abrir o item que precisa de conserto.",
      // O problema que é do arquivo inteiro, e não de um país ou clube.
      raiz: "arquivo",
    },

    /*
     * Como chamar um item do banco que ainda não tem nome.
     *
     * Quem está montando um banco cria o clube antes de batizá-lo, e o endereço
     * do problema precisa continuar dizendo onde ele fica. O número é a posição
     * na lista, contada de um, que é o que a árvore da esquerda mostra.
     */
    lugar: {
      pais: "País {numero}",
      divisao: "Divisão {numero}",
      clube: "Clube {numero}",
      jogador: "Jogador {numero}",
    },
  },

  atalhos: {
    aria: "Atalhos de teclado",
    rotulo: "Teclado",
    titulo: "Atalhos",
    fecheComEsc: "feche com Esc",
    explicacao:
      "A rodada inteira cabe no teclado. Enquanto você digita em um campo, nenhuma tecla vira atalho: Esc sai do campo e devolve o teclado ao jogo. Com um painel aberto, valem só as teclas daquele painel.",
    emBreve: "em breve",
    voltarAoJogo: "Voltar ao jogo",

    ligacaoAte: "a",

    grupos: {
      rodada: "Rodada",
      secoes: "Seções",
      navegacao: "Navegação",
      hotseat: "Hotseat",
      aoVivo: "Na partida ao vivo",
      painelAberto: "Com um painel aberto",
    },

    descricoes: {
      jogar: "Jogar a rodada",
      elenco: "Elenco",
      tatica: "Tática",
      mercado: "Mercado",
      clube: "Clube",
      competicoes: "Competições",
      noticias: "Notícias",
      financas: "Caixa",
      carreira: "Carreira",
      busca: "Buscar jogador: abre o mercado com o cursor na busca",
      lista: "Andar pela lista da tela",
      abrir: "Abrir a linha marcada",
      voltar: "Fechar o painel aberto ou voltar ao elenco",
      ajuda: "Mostrar esta lista",
      assento: "Passar a vez para outro treinador do hotseat",
      pausar: "Pausar ou seguir o jogo",
      ritmo: "Ritmo 1x, 2x ou 4x",
      pular: "Pular para o resultado",
      fecharPainel: "Fechar o painel de cima",
      continuar: "Continuar no resumo da partida e no fim de temporada",
      cartao: "Gerar o cartão para compartilhar",
    },
  },

  // -------------------------------------------------------------------------
  // Loja
  // -------------------------------------------------------------------------
  loja: {
    titulo: "Loja",
    voltar: "Voltar",
    atualizar: "Atualizar",
    carregarMais: "Carregar mais",

    /*
     * Servidor sem credencial de provedor. Não é falha de quem está jogando,
     * é estado desta instalação, e o texto precisa dizer isso sem alarme.
     */
    semPagamento: "As compras ainda não estão disponíveis neste servidor.",
    semPagamentoCorpo:
      "Quem cuida deste servidor ainda não ligou um meio de pagamento. O catálogo fica aqui para consulta, e o que você já comprou continua valendo.",
    semPagamentoMetodo: "O meio pedido foi {metodo}.",

    catalogo: "O que dá para comprar",
    semProdutos: "Este servidor não tem nada no catálogo por enquanto.",

    grupos: {
      lifetime: "Licença vitalícia",
      passe: "Passe Legacy",
      cosmetic: "Cosméticos",
      datapack: "Bancos de dados",
    },

    tipos: {
      lifetime: "Compra única",
      pass_month: "Assinatura mensal",
      pass_year: "Assinatura anual",
      cosmetic: "Item cosmético",
      datapack: "Banco de dados",
    },

    duracaoUmMes: "Um mês de acesso por cobrança",
    duracaoMeses: "{meses} meses de acesso por cobrança",
    semPrazo: "Sem prazo de validade",
    semPreco: "Sem preço em {moeda}",
    jaTem: "Você já tem",

    comprarCartao: "Pagar com cartão",
    comprarPix: "Pagar com Pix",
    abrindo: "Abrindo...",
    metodoFora: "Este servidor não oferece pagamento por {metodo}.",
    metodos: {
      card: "cartão",
      pix: "Pix",
      android: "loja do Android",
      ios: "loja da Apple",
    },

    checkoutAberto: "Abrimos a página de pagamento em outra aba. Termine por lá e volte aqui.",
    checkoutSemAba:
      "O navegador bloqueou a aba nova. Use o botão abaixo para abrir o pagamento.",
    abrirPagamento: "Abrir o pagamento",
    fecharCobranca: "Fechar",

    pixTitulo: "Pix de {valor}",
    pixInstrucao: "Aponte a câmera para o código ou use o copia e cola.",
    pixCopiar: "Copiar o código",
    pixCopiado: "Código copiado.",
    pixExpira: "Este código vale até {data}.",
    pixEspera: "Assim que o pagamento cair, o direito aparece sozinho na sua conta.",

    recibos: "Seus recibos",
    semRecibos: "Nenhuma compra registrada ainda.",
    reciboLinha: "{situacao} · {origem} · {data}",
    situacoes: {
      pending: "aguardando pagamento",
      paid: "pago",
      refunded: "estornado",
      expired: "vencido",
    },

    assinatura: "Assinatura",
    assinaturaAtiva: "Passe Legacy ativo.",
    assinaturaRenova: "Renova em {data}.",
    assinaturaSemRenovacao: "Sem renovação automática.",
    assinaturaSemPasse: "Você não tem assinatura ativa para cancelar.",
    cancelar: "Cancelar a renovação",
    cancelando: "Cancelando...",
    cancelarAjuda:
      "Cancelar é parar de renovar, não perder o que já foi pago: o passe vale até o fim do período.",
    cancelado: "Renovação cancelada em {total} assinatura(s).",
    canceladoAte: "O passe continua valendo até {data}.",
    cancelarNaLoja:
      "Esta assinatura foi feita dentro da loja do aparelho, e é lá que ela termina. Abra as assinaturas da loja para concluir.",

    restaurar: "Restaurar compras",
    restaurando: "Conferindo...",
    restaurado: "Direitos conferidos com o servidor.",
    restaurarAjuda:
      "Comprou em outro aparelho, pelo site ou pela loja? Isto relê no servidor os direitos desta conta.",

    vestiario: "Seu vestiário",
    vestiarioAjuda:
      "Escolha o que usar em cada lugar. Dá para trocar quando quiser, e trocar não custa nada.",
    vestiarioVazio: "Você ainda não tem cosmético nenhum. O que comprar aqui aparece nesta lista.",
    vestiarioErro: "Não consegui carregar o seu vestiário.",
    usar: "Usar",
    emUso: "Em uso",
    tirar: "Tirar",
    trocando: "Trocando...",
    trocado: "Pronto, o visual mudou.",
    ondeAparece: {
      crest: "Aparece no escudo do seu clube, na carreira e nas ligas online.",
      kit: "Aparece ao lado do escudo, no cabeçalho do seu clube.",
      stadium: "Pinta o cabeçalho do clube com as cores do estádio.",
      title: "Aparece ao lado do seu nome na lista de treinadores das ligas.",
    },
  },

  // -------------------------------------------------------------------------
  // Cosméticos
  // -------------------------------------------------------------------------
  cosmeticos: {
    tipos: {
      crest: "Escudo",
      kit: "Uniforme",
      stadium: "Tema de estádio",
      title: "Título de treinador",
    },
    // Sigla que aparece dentro do escudo na vitrine, onde ainda não existe
    // clube nenhum para emprestar a dele.
    siglaDaPrevia: "LM",
    tituloAjuda: "Título de treinador, comprado na loja. Não muda nada em campo.",
  },

  // -------------------------------------------------------------------------
  // Mercado entre treinadores humanos
  // -------------------------------------------------------------------------
  mercadoOnline: {
    titulo: "Mercado da liga",
    voltar: "Voltar",
    atualizar: "Atualizar",
    carregarMais: "Carregar mais",

    semClube: "Você ainda não tem clube nesta liga, então não há o que negociar.",
    mercadoDesligado: "Esta liga não permite negócio entre treinadores.",

    abas: {
      recebidas: "Recebidas",
      enviadas: "Enviadas",
      leiloes: "Leilões",
      historico: "Histórico",
    },

    filtros: {
      abertas: "Abertas",
      aguardando: "Esperando o comissário",
      todas: "Todas",
      leiloesAbertos: "Abertos",
      leiloesFechados: "Fechados",
      leiloesTodos: "Todos",
    },

    situacoes: {
      open: "aberta",
      accepted: "aceita",
      rejected: "recusada",
      countered: "contraposta",
      expired: "vencida",
      vetoed: "vetada",
    },

    direcoes: {
      in: "chegou para você",
      out: "você propôs",
    },

    semPropostas: "Nenhuma proposta por aqui.",
    semLeiloes: "Nenhum leilão por aqui.",

    linhaValor: "Valor",
    linhaParcelas: "Parcelas",
    linhaPrazo: "Fecha na rodada {rodada}",
    linhaPrazoVencido: "Prazo vencido na rodada {rodada}",
    linhaTroca: "Com {jogador} na troca",
    linhaEmprestimo: "Empréstimo até a temporada {temporada}, com {percentual}% do salário",
    linhaContraproposta: "Pedido da contraproposta: {valor}",
    naFila: "Já está na fila desta rodada",
    esperandoComissario: "Esperando o aval do comissário",

    aceitar: "Aceitar",
    recusar: "Recusar",
    contrapor: "Contrapor",
    enviando: "Enviando...",

    /*
     * A contraproposta troca os lados da mesa, e isso muda quem responde.
     * Quem não entende isso acha que contrapôs e ficou esperando o outro,
     * quando na verdade agora é ele quem está à espera.
     */
    contraporTitulo: "Contrapor: a negociação muda de lado",
    contraporExplicacao:
      "A proposta que chegou é encerrada e nasce uma sua, no sentido contrário. Quem responde a partir de agora é o outro treinador, e o prazo recomeça.",
    contraporValor: "Quanto você quer",
    contraporEnviar: "Enviar a contraproposta",
    contrapostaEnviada:
      "Contraproposta enviada. A proposta original virou {situacao}, e agora quem responde é o outro treinador.",

    aceitaNaFila: "Negócio fechado. A transferência entra na rodada {rodada}.",
    aceitaEsperando:
      "Negócio fechado entre vocês dois. Falta o aval do comissário para entrar na rodada.",
    recusadaOk: "Proposta recusada.",

    rodada: "Rodada",
    situacaoDaLiga: "Situação",
    avalDoComissario: "Aval do comissário",
    avalExigido: "Exigido",
    avalDispensado: "Dispensado",

    aprovar: "Liberar",
    aprovada: "Liberada. A transferência entra na rodada {rodada}.",
    vetar: "Vetar",
    vetoMotivo: "Motivo do veto, se quiser dizer",
    vetoEnviar: "Confirmar o veto",
    vetada: "Proposta vetada, e a transferência saiu da fila.",
    aprovacaoLigada:
      "Nesta liga todo negócio passa pelo comissário antes de entrar na rodada.",

    leilaoLanceMinimo: "Lance mínimo",
    leilaoMeuLance: "Seu lance",
    leilaoSemLance: "Você ainda não deu lance",
    leilaoClubes: "{total} clube(s) na disputa",
    leilaoFechado: "Fechado com {valor}",
    leilaoSemVencedor: "Fechado sem vencedor",
    leilaoLance: "Quanto você paga",
    leilaoDarLance: "Dar o lance",
    leilaoLanceFeito: "Lance de {valor} registrado. Ele fecha na rodada {rodada}.",
    leilaoFechadoAjuda:
      "O lance é fechado: você vê quantos clubes entraram e o seu valor, nunca o dos outros. Quem vence é decidido na virada da rodada.",

    novaTitulo: "Fazer uma proposta",
    novaJogador: "Jogador",
    novaEscolha: "Escolha um jogador de outro treinador",
    novaBusca: "Buscar pelo nome",
    novaSemJogadores: "Nenhum jogador de outro treinador está disponível agora.",
    naLista: "na lista",
    novaValor: "Quanto você paga",
    novaParcelas: "Em quantas parcelas",
    novaParcela: "{total}x",
    novaEnviar: "Enviar a proposta",
    novaEnviada: "Proposta enviada para {clube}.",
    novaVirouLeilao:
      "Outro clube também quer este jogador, então a disputa virou leilão. A sua proposta entrou como lance.",
    novaPiso: "O piso desta liga é {percentual}% do valor do jogador.",
  },

  // -------------------------------------------------------------------------
  // Ranqueado
  // -------------------------------------------------------------------------
  ranqueado: {
    titulo: "Ranqueado",
    voltar: "Voltar",
    atualizar: "Atualizar",
    carregarMais: "Carregar mais",

    temporada: "Temporada {numero}",
    temporadaSituacao: "Situação",
    temporadaEstados: {
      upcoming: "ainda vai começar",
      running: "em andamento",
      finished: "encerrada",
    },
    temporadaPeriodo: "De {inicio} até {fim}",
    temporadaSemanas: "{semanas} semanas",
    temporadaTreinadores: "{total} treinadores por liga",

    meuMmr: "Seu MMR",
    minhaDivisao: "Sua divisão",
    divisoes: "Divisões",
    divisaoFaixa: "a partir de {mmr} de MMR",

    fila: "Fila",
    filaAjuda:
      "A liga é montada assim que houver gente suficiente com MMR parecido. Dá para sair da fila a qualquer momento.",
    entrarNaFila: "Entrar na fila",
    entrando: "Entrando...",
    sairDaFila: "Sair da fila",
    saindo: "Saindo...",
    naFila: "Você está na fila desde {quando}.",
    naFilaPosicao: "Posição {posicao} de {total} na região {regiao}.",
    naFilaFaltam: "Faltam {quantos} para fechar uma liga.",
    naFilaCompleta: "Gente suficiente na fila. A liga sai na próxima entrada.",
    foraDaFila: "Você não está na fila.",
    saiuDaFila: "Você saiu da fila.",
    regiao: "Região",

    ligaPronta: "Sua liga ranqueada está montada.",
    abrirLiga: "Abrir a liga",
    emDisputa: "Temporada em curso",
    minhaEntrada: "Divisão {divisao}, MMR de entrada {mmr}.",
    minhaPosicao: "Posição {posicao} na tabela.",
    semPosicaoAinda: "A posição final sai no fim da temporada.",
    semEntrada: "Você ainda não disputa uma liga nesta temporada.",

    /*
     * O ranqueado é do Passe, e o servidor recusa a fila com
     * `ENTITLEMENT_REQUIRED`. A tela não esconde o modo: mostra a temporada e o
     * leaderboard, e diz na hora certa o que falta.
     */
    exigePasse: "A fila ranqueada faz parte do Passe Legacy.",
    irParaLoja: "Ver na loja",

    leaderboard: "Leaderboard",
    leaderboardTodas: "Todas as divisões",
    semLeaderboard: "Ninguém pontuou nesta temporada ainda.",
    colunaPosicao: "#",
    colunaTreinador: "Treinador",
    colunaMmr: "MMR",
    colunaDivisao: "Divisão",
    colunaNaLiga: "Na tabela",
    voce: "você",

    historico: "Histórico",
    semHistorico: "Nenhuma temporada ranqueada disputada ainda.",
    historicoLinha: "Temporada {numero} · divisão {divisao}",
    historicoVariacao: "MMR de {inicio} para {fim}",
    historicoSemFim: "MMR de entrada {inicio}, temporada ainda aberta",
    historicoPosicao: "Terminou em {posicao}.",
  },



  // -------------------------------------------------------------------------
  // Texto que vem do motor
  //
  // O motor não escreve frase nenhuma: ele grava chave e parâmetros dentro do
  // save, e a frase é esta aqui. Por isso mudar a redação deste bloco muda
  // também a notícia que já está guardada numa carreira antiga, coisa que era
  // impossível quando a frase pronta ia para o save.
  //
  // Duas regras valem em todo este bloco:
  //
  // 1. Os marcadores de cada frase são exatamente os que o motor manda, nem um
  //    a mais nem um a menos. Existe teste que quebra quando divergem, e ele
  //    existe porque marcador perdido deixa a notícia sem o nome ou sem o valor.
  // 2. Um parâmetro cujo valor começa por `@` é outra chave daqui de dentro, de
  //    `rotulos`. É assim que o rótulo do clássico entra no meio da frase sem o
  //    motor precisar saber escrevê-lo em três línguas.
  // -------------------------------------------------------------------------
  motor: {
    noticias: {
      resultado: {
        classicoVitoria: {
          titulo: "Clássico: vitória sobre o {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante} x {golsVisitante} {visitante} pelo {torneio}. A torcida vai lembrar desta vitória sobre o {adversarioNome} por algumas semanas.",
        },
        classicoVitoriaGrande: {
          titulo: "Clássico: vitória sobre o {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante} x {golsVisitante} {visitante} pelo {torneio}. A torcida vai lembrar desta vitória sobre o {adversarioNome} por um bom tempo.",
        },
        classicoEmpate: {
          titulo: "Clássico: empate com o {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante} x {golsVisitante} {visitante} pelo {torneio}. Contra o {adversarioNome} ninguém sai satisfeito com um empate.",
        },
        classicoDerrota: {
          titulo: "Clássico: derrota para o {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante} x {golsVisitante} {visitante} pelo {torneio}. Perder para o {adversarioNome} custa mais caro do que os três pontos.",
        },
        classicoDerrotaGrande: {
          titulo: "Clássico: derrota para o {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante} x {golsVisitante} {visitante} pelo {torneio}. Perder para o {adversarioNome} custa muito mais caro do que os três pontos.",
        },
      },

      elenco: {
        suspensaoPorCartoes: {
          titulo: "Suspensão por cartões",
          corpo:
            "{jogador} completou {amarelos} amarelos e cumpre suspensão na próxima rodada.",
        },
        deVoltaDaSelecao: {
          titulo: "De volta da seleção",
          corpo: "{jogador} voltou da seleção e está à disposição.",
        },
        climaPesado: {
          titulo: "Clima pesado no vestiário",
          corpo:
            "{primeiro} e {segundo} discutiram no treino. O elenco tem estrelas demais e nenhum líder para segurar o grupo.",
        },
        pedidoDeSaida: {
          titulo: "Pedido de saída",
          corpo: "{jogador} pediu para deixar o clube e está na lista de transferências.",
        },
        escalacaoRecusada: {
          titulo: "Escalação recusada",
          corpo: "A escalação não foi aceita: {motivo}",
        },
        conversaComOElenco: {
          titulo: "Conversa com o elenco",
          corpo: "Do vestiário: {recado}",
        },
        renovacaoRecusada: {
          titulo: "Renovação recusada",
          corpo: "{jogador} achou a proposta baixa e recusou a renovação.",
        },
        dispensa: {
          titulo: "Dispensa",
          corpo: "{jogador} foi dispensado. A rescisão custou {valor}.",
        },
        elogioAceito: {
          titulo: "Conversa com o elenco",
          corpo: "{jogador} agradeceu o elogio.",
        },
        broncaMalRecebida: {
          titulo: "Conversa com o elenco",
          corpo: "{jogador} não gostou nada da bronca.",
        },
        broncaAceita: {
          titulo: "Conversa com o elenco",
          corpo: "{jogador} ouviu a bronca e prometeu resposta em campo.",
        },
        garotoDeEmergencia: {
          titulo: "Garoto promovido de emergência",
          corpo: "Com o elenco curto, {jogador} foi promovido da base.",
        },
        aposentadoria: {
          titulo: "Aposentadoria",
          corpo:
            "{jogador} pendurou as chuteiras aos {idade} anos, depois de {jogos} jogos e {gols} gols.",
        },
        basePedeDecisaoUm: {
          titulo: "A base pede uma decisão",
          corpo:
            "{jogador} chegou à idade de subir. Promova, segure mais um ano ou dispense antes de a temporada começar.",
        },
        basePedeDecisao: {
          titulo: "A base pede decisões",
          corpo:
            "{total} garotos chegaram à idade de subir: {jogadores}. Promova, segure mais um ano ou dispense antes de a temporada começar.",
        },
        basePerdeuGaroto: {
          titulo: "A base perdeu um garoto",
          corpo:
            "{jogador} completou {idade} anos sem subir e foi procurar futebol em outro clube.",
        },
        promovidoDaBase: {
          titulo: "Promovido da base",
          corpo: "{jogador} assinou com o profissional e passa a ocupar vaga no elenco.",
        },
        dispensadoDaBase: {
          titulo: "Dispensado da base",
          corpo: "{jogador} foi dispensado e não faz mais parte do clube.",
        },
        baseRecusou: {
          titulo: "A base esperava outra coisa",
          corpo: "A decisão não valeu: {motivo}",
        },
      },

      lesao: {
        jogadorLesionado: {
          titulo: "Jogador lesionado",
          corpo: "{jogador} se lesionou e fica fora por cerca de {rodadas} rodada(s).",
        },
        recuperado: {
          titulo: "Recuperado",
          corpo: "{jogador} está liberado para jogar.",
        },
      },

      financeiro: {
        obraConcluida: {
          titulo: "Obra concluída",
          corpo: "A obra ficou pronta e a estrutura chegou ao nível {nivel}.",
        },
        obraEmAndamento: {
          titulo: "Obra em andamento",
          corpo: "Termine a obra atual antes de começar outra.",
        },
        obraInvalida: {
          titulo: "Obra inválida",
          corpo: "As estruturas sobem um nível de cada vez.",
        },
        obraInviavel: {
          titulo: "Obra inviável",
          corpo: "A obra custa {custo} e o caixa não cobre.",
        },
        emprestimoNegado: {
          titulo: "Empréstimo negado",
          corpo: "O banco não liberou: {motivo}",
        },
      },

      diretoria: {
        vendaForcada: {
          titulo: "Venda forçada",
          corpo:
            "Com o caixa no vermelho, a diretoria vendeu {jogador} por {valor} sem consultar a comissão técnica.",
        },
        novoComando: {
          titulo: "Novo comando",
          corpo: "{treinador} assume o {clube}. O que a diretoria pede: {objetivo}.",
        },
        bemVindoAoClube: {
          titulo: "Bem-vindo ao clube",
          corpo: "{treinador} assume o comando do {clube}. O objetivo da temporada: {objetivo}.",
        },
        pressao: {
          titulo: "Pressão da diretoria",
          corpo:
            "O objetivo: {objetivo}. O time está em {posicaoNaTabela}º e a paciência está acabando.",
        },
        demissao: {
          titulo: "Você foi demitido",
          corpo:
            "A diretoria pedia: {objetivo}. O time terminou em {posicao}º e o seu contrato não foi renovado.",
        },
      },

      mercado: {
        contratacaoLivre: {
          titulo: "Contratação fechada",
          corpo: "{jogador} ({posicao}, força {forca}) chega ao {clube} sem custos.",
        },
        contratacaoAVista: {
          titulo: "Contratação fechada",
          corpo: "{jogador} ({posicao}, força {forca}) chega ao {clube} por {valor}.",
        },
        contratacaoParcelada: {
          titulo: "Contratação fechada",
          corpo:
            "{jogador} ({posicao}, força {forca}) chega ao {clube} por {valor} em {parcelas} parcelas.",
        },
        vendaLivre: {
          titulo: "Jogador vendido",
          corpo: "{jogador} deixou o {vendedor} rumo ao {comprador} sem custos.",
        },
        vendaAVista: {
          titulo: "Jogador vendido",
          corpo: "{jogador} deixou o {vendedor} rumo ao {comprador} por {valor}.",
        },
        vendaParcelada: {
          titulo: "Jogador vendido",
          corpo:
            "{jogador} deixou o {vendedor} rumo ao {comprador} por {valor} em {parcelas} parcelas.",
        },
        emprestimoFechado: {
          titulo: "Empréstimo fechado",
          corpo:
            "{jogador} ({posicao}, força {forca}) joga pelo {clube} até o fim da temporada, e o clube assume {parte}% do salário.",
        },
        jogadorEmprestado: {
          titulo: "Jogador emprestado",
          corpo:
            "{jogador} ({posicao}, força {forca}) foi emprestado ao {clube} até o fim da temporada, que assume {parte}% do salário.",
        },
        fimDeEmprestimo: {
          titulo: "Fim de empréstimo",
          corpo: "{jogador} volta ao {dono} depois da temporada no {emprestador}.",
        },
        relatorioDeOlheiro: {
          titulo: "Relatório de olheiro",
          corpo: "{jogador} foi observado. Potencial estimado entre {minimo} e {maximo}. {veredito}",
        },
        propostaExpirada: {
          titulo: "Proposta expirada",
          corpo: "A proposta por {jogador} caducou sem resposta.",
        },
        disputaVencida: {
          titulo: "Disputa vencida",
          corpo: "{clubes} clubes disputavam {jogador}. A maior oferta levou.",
        },
        janelaFechada: {
          titulo: "Janela fechada",
          corpo: "O mercado está fechado nesta rodada.",
        },
        caixaInsuficiente: {
          titulo: "Caixa insuficiente",
          corpo: "A entrada de {entrada} passa do caixa disponível.",
        },
        emprestimoRecusado: {
          titulo: "Empréstimo recusado",
          corpo: "O empréstimo não saiu: {motivo}",
        },
        olheiroParado: {
          titulo: "Olheiro parado",
          corpo: "O olheiro não saiu: {motivo}",
        },
        contratoEncerrado: {
          titulo: "Contrato encerrado",
          corpo: "{jogador} deixou o {clube} em fim de contrato e está livre no mercado.",
        },
      },

      competicao: {
        gruposEncerrados: {
          titulo: "Fase de grupos da {competicao} encerrada",
          corpo: "{clubes} clubes seguem vivos rumo ao título continental.",
        },
        campeaoDaCopa: {
          titulo: "{clube} é campeão",
          corpo: "{clube} conquistou a {competicao}.",
        },
        temporadaComeca: {
          titulo: "Temporada {temporada} começa",
          corpo: "São {rodadas} rodadas até conhecermos os campeões.",
        },
        campeaoDaLiga: {
          titulo: "{clube} conquista a {competicao}",
          corpo:
            "Com {pontos} pontos em {jogos} jogos, o {clube} é o campeão da temporada {temporada}.",
        },
        acesso: {
          titulo: "Acesso conquistado",
          corpo: "O {clube} sobe para a {divisao}.",
        },
        rebaixamento: {
          titulo: "Rebaixamento",
          corpo: "O {clube} cai para a {divisao}.",
        },
        nacoesJogo: {
          titulo: "Copa das Nações: {mandante} {golsMandante} x {golsVisitante} {visitante}",
          corpo: "{fase}, quem avança é {vencedor}.",
        },
        nacoesJogoNosPenaltis: {
          titulo: "Copa das Nações: {mandante} {golsMandante} x {golsVisitante} {visitante}",
          corpo: "{fase}, a vaga saiu nos pênaltis. Quem avança é {vencedor}.",
        },
        nacoesCampea: {
          titulo: "{selecao} vence a Copa das Nações",
          corpo: "O título fica com a seleção. A competição volta daqui a {temporadas} temporadas.",
        },
      },
    },

    // Pedaço de frase que o motor manda como `@chave`, e rótulo que a tela pede
    // direto. Nenhum tem marcador: rótulo com valor dentro seria uma frase.
    rotulos: {
      rivalidade: {
        historica: "Rivalidade histórica",
        regional: "Clássico regional",
        recente: "Rivalidade recente",
      },
      olheiro: {
        vaiCrescer: "Tem muito a crescer e vale a aposta.",
        quaseFeito: "Ainda evolui, mas já é quase o que vai ser.",
        noTeto: "Está no teto dele. O que você vê é o que você leva.",
      },
      reputacao: {
        "1": "Municipal",
        "2": "Estadual",
        "3": "Regional",
        "4": "Nacional",
        "5": "Continental",
        "6": "Mundial",
      },
      objetivo: {
        titulo: "Ser campeão",
        brigarPeloTitulo: "Brigar pelo título",
        topQuatro: "Terminar entre os quatro primeiros",
        acesso: "Conseguir o acesso",
        meioDeTabela: "Terminar no meio de tabela",
        fugirDoRebaixamento: "Escapar do rebaixamento",
      },
      // A fase da Copa das Nações entra na notícia com a preposição junto, e é
      // por isso que ela aparece aqui e não no molde da frase: "em" contrai com
      // o artigo e o artigo muda de número ("na final", "nas quartas"), então o
      // molde fixo dava "Na quartas de final". O inglês e o espanhol já faziam
      // assim, cada um do seu jeito. A maiúscula é do começo da oração, que é o
      // único lugar em que estes rótulos aparecem.
      nacoes: {
        final: "Na final",
        semifinal: "Na semifinal",
        quartas: "Nas quartas de final",
        oitavas: "Nas oitavas de final",
        faseInicial: "Na fase inicial",
      },
      // A fase de um mata-mata de clubes. A chave é o identificador que o save
      // guarda em `Fixture.stage`; o nome escrito vive aqui.
      fase: {
        preliminar: "Fase preliminar",
        inicial: "Fase inicial",
        primeira: "Primeira fase",
        segunda: "Segunda fase",
        oitavas: "Oitavas de final",
        quartas: "Quartas de final",
        semifinal: "Semifinal",
        final: "Final",
        grupo: "Grupo {letra}",
        deClubes: "Fase de {clubes}",
      },
      estrutura: {
        stadium: "Estádio",
        youth: "Categorias de base",
        training: "Centro de treinamento",
        medical: "Departamento médico",
      },
    },

    // O negócio do reforço, resumido para o cartão de compartilhar. Sai dos
    // parâmetros da notícia, e não de fatiar a frase dela como antes.
    // A narração da partida, lance a lance. É o texto que mais aparece na frente
    // de quem joga: passa ao vivo e volta inteiro no relatório.
    lances: {
      golArmacao: "{jogador} conclui a jogada trabalhada.",
      golArmacaoAssistido: "{jogador} conclui a jogada trabalhada, com assistência de {assistente}.",
      golDrible: "{jogador} passa pela marcação e finaliza.",
      golDribleAssistido: "{jogador} passa pela marcação e finaliza, com assistência de {assistente}.",
      golCruzamento: "{jogador} aparece na área e cabeceia.",
      golCruzamentoAssistido: "{jogador} aparece na área e cabeceia o cruzamento de {assistente}.",
      golContraAtaque: "{jogador} finaliza no contra-ataque.",
      golContraAtaqueAssistido:
        "{jogador} finaliza no contra-ataque, com assistência de {assistente}.",
      golBolaParada: "{jogador} marca na bola parada.",
      golPenalti: "{jogador} converte o pênalti.",
      penaltiPerdido: "{jogador} desperdiça o pênalti.",
      defesa: "{goleiro} defende a finalização de {jogador}.",
      paraFora: "{jogador} finaliza e erra o alvo.",
      vermelhoDireto: "{jogador} é expulso direto.",
      segundoAmarelo: "{jogador} recebe o segundo amarelo e está expulso.",
      amarelo: "{jogador} leva cartão amarelo.",
      lesao: "{jogador} sente uma lesão e precisa de atendimento.",
      substituicao: "{entra} entra no lugar de {sai}.",
      decisaoPorPenaltis:
        "Decisão por pênaltis: {mandante} {golsMandante} x {golsVisitante} {visitante}.",
    },

    // Por que o motor recusou uma ordem. Sai por dois caminhos: direto na tela,
    // quando o clique foi agora, e como `{motivo}` de uma notícia, quando a
    // ordem veio da fila de decisões.
    motivos: {
      escalacao: {
        faltamTitulares: "A escalação precisa de 11 titulares.",
        jogadorRepetido: "Um jogador aparece duas vezes na escalação.",
        jogadorDesconhecido: "Há um jogador desconhecido na escalação.",
        deOutroClube: "Há um escalado que não pertence ao clube.",
        lesionado: "{jogador} está lesionado.",
        suspenso: "{jogador} está suspenso.",
        naSelecao: "{jogador} está com a seleção e não pode jogar por enquanto.",
        semGoleiro: "Escale ao menos um goleiro.",
        titularNoBanco: "Um titular também está no banco.",
        reservaInvalido: "Há um reserva que não pertence ao clube.",
        bancoCheio: "O banco aceita no máximo {maximo} jogadores.",
      },
      emprestimo: {
        jogadorDesconhecido: "Esse jogador não existe.",
        jogadorLivre: "Jogador livre não se empresta, se contrata.",
        jaEmprestado: "Ele já está emprestado.",
        jaEDoClube: "Ele já é do seu clube.",
        clubeDesconhecido: "Esse clube não existe.",
        elencoDoDonoCurto: "O {clube} ficaria com elenco curto demais.",
        elencoCheio: "Seu elenco já está cheio.",
        contratoAcabando: "O contrato dele acaba nesta temporada.",
        salarioInsuficiente: "O {clube} quer repassar pelo menos {parte}% do salário.",
      },
      base: {
        garotoSumiu: "Esse garoto não está mais na base.",
        elencoCheio: "O elenco já está no limite de {maximo} jogadores.",
        idadeNoLimite: "{jogador} não pode passar dos {idade} anos na base.",
      },
      observacao: {
        jogadorDesconhecido: "Esse jogador não existe.",
        doProprioElenco: "Você não precisa de olheiro para o próprio elenco.",
        jaEntregue: "Seu olheiro já entregou esse relatório nesta temporada.",
        semCaixa: "Observar esse jogador custa {custo}.",
      },
      banco: {
        valorInvalido: "Valor inválido.",
        limiteDoBanco: "O banco libera no máximo {limite}.",
      },
      emprego: {
        treinadorDesconhecido: "Treinador não encontrado.",
        soHumanoTroca: "Só treinador humano troca de clube por aqui.",
        foraDaJanela: "A troca de clube só acontece entre temporadas.",
        clubeDesconhecido: "Clube não encontrado.",
        semProposta: "Esse clube não fez proposta a você.",
        clubeOcupado: "Esse clube já tem treinador.",
      },
    },

    // As observações soltas do laudo de olheiro, uma por linha.
    laudo: {
      lider: "Lidera o vestiário.",
      profissional: "Profissional exemplar no dia a dia.",
      ambicioso: "Ambicioso: quer jogar e quer crescer.",
      temperamental: "Temperamental, e isso aparece em campo.",
      mercenario: "Mercenário: vai atrás do melhor salário.",
      tranquilo: "Tranquilo, não dá trabalho ao grupo.",
      lesoesPreocupam: "Histórico de lesões preocupa.",
      fisicoNaMedia: "Fisicamente dentro da média.",
      raramenteSeMachuca: "Raramente se machuca.",
      idadeAFavor: "Idade a favor: dá tempo de formar.",
      contratoPertoDoFim: "Contrato perto do fim. Dá para negociar melhor.",
    },

    // Nome e descrição de cada conquista de carreira. O motor manda a chave; o
    // identificador dela, que é o que o save guarda, nunca aparece na tela.
    conquistas: {
      primeiroTitulo: { nome: "Primeiro título", descricao: "Levante a primeira taça da carreira." },
      campeaoNacional: { nome: "Campeão nacional", descricao: "Vença a primeira divisão de um país." },
      donoDoEstado: { nome: "Dono do estado", descricao: "Vença um campeonato estadual." },
      campeaoDaCopa: { nome: "Mata-mata é outro jogo", descricao: "Vença uma copa nacional." },
      campeaoContinental: { nome: "Do outro lado do continente", descricao: "Vença a competição continental." },
      invicto: { nome: "Ninguém passou", descricao: "Termine um campeonato de pontos corridos sem perder nenhuma partida." },
      contasEmDia: { nome: "Contas em dia", descricao: "Feche a temporada no lucro e sem dever nada ao banco." },
      goleador: { nome: "Artilheiro de respeito", descricao: "Tenha um jogador com 25 gols ou mais numa temporada." },
      superouAMeta: { nome: "Acima do combinado", descricao: "Termine bem acima do que a diretoria pedia." },
      tricampeao: { nome: "Tricampeão", descricao: "Vença a mesma competição três vezes." },
      dinastia: { nome: "Dinastia", descricao: "Some dez títulos na carreira." },
      casaEComida: { nome: "Casa e comida", descricao: "Complete cinco temporadas seguidas no mesmo clube." },
      andarilho: { nome: "Passaporte carimbado", descricao: "Trabalhe em clubes de três países diferentes." },
      doFundoDoPoco: { nome: "Do fundo do poço", descricao: "Suba de divisão comandando um clube fora da elite." },
    },

    // O que há de errado num banco de dados, para quem o editou.
    datapack: {
      naoEDatapack: "O arquivo não é um datapack.",
      formatoDiferente: "Este jogo lê datapacks no formato {esperado}, e o arquivo diz {encontrado}.",
      semPais: "O datapack precisa de pelo menos um país.",
      siglaDoPais: "O país precisa de uma sigla de até {maximo} letras.",
      siglaRepetida: "A sigla {sigla} aparece duas vezes.",
      semNomeDoPais: "Falta o nome do país.",
      semDivisao: "O país precisa de pelo menos uma divisão.",
      divisaoCurta: "Uma divisão precisa de pelo menos {minimo} clubes para ter calendário.",
      divisaoImpar: "Divisão com número ímpar de clubes deixa alguém sem jogo em toda rodada.",
      semNomeDoClube: "Falta o nome do clube.",
      elencoCurto: "O {clube} precisa de pelo menos {minimo} jogadores para escalar um time.",
      semGoleiro: "O {clube} não tem goleiro.",
    },

    // Por que um save não abriu. São os três becos sem saída da migração, e o
    // único erro do motor que toma a tela inteira: quando ele aparece, não há
    // carreira para voltar.
    save: {
      semEsquema: "Este arquivo não é um save do Legacy Manager.",
      deVersaoMaisNova:
        "Este save foi gravado por uma versão mais nova do jogo (esquema {esquema}). Atualize o aplicativo para abrir a carreira.",
      semMigracao:
        "Este save está no esquema {esquema} e o jogo não sabe atualizá-lo. Ele pode ter sido editado à mão.",
    },

    cartaoDeContratacao: {
      semCusto: "Sem custos",
      aVista: "Por {valor}",
      parcelado: "Por {valor} em {parcelas} parcelas",
    },

    // O "Por que aconteceu" da partida.
    fatores: {
      posse: "O meio-campo do {clube} dominou a partida, com {posse}% de posse.",
      eficiencia:
        "O {melhor} foi mais eficiente: {golsDoMelhor} gol(s) em {chancesDoMelhor} chances, contra {golsDoPior} em {chancesDoPior} do {pior}.",
      decisivo: "{jogador} decidiu com {gols} gols.",
      expulsao: "A expulsão aos {minuto} minutos deixou o {clube} com um a menos.",
      setor: "O ataque do {forte} era claramente superior à defesa do {fraco}.",
      cansaco: "{jogadores} jogadores entraram em campo abaixo de 55% de condição física.",
      truncado: "Jogo truncado, com pouquíssimas chances criadas dos dois lados.",
      semGol: "Muita chance e nenhum gol: {chances} oportunidades desperdiçadas no total.",
      equilibrado: "Partida equilibrada do começo ao fim, sem nenhum time conseguir se impor.",
      detalhes: "Jogo parelho, decidido em detalhes a favor do {clube}.",
    },
  },

  // -------------------------------------------------------------------------
  // Erros da API
  //
  // A resposta de erro traz código estável e mensagem (docs/API.md 1). Quem vai
  // para a tela é o texto daqui, escolhido pelo código, e não a mensagem do
  // servidor: ela vem sempre em português, e uma tela em inglês com um aviso em
  // português parece defeito. Código que este bloco não conhece cai de volta na
  // mensagem do servidor, que é melhor que uma tela muda.
  // -------------------------------------------------------------------------
  errosDaApi: {
    VALIDATION: "Tem campo fora do esperado. Confira o que você preencheu.",
    UNAUTHENTICATED: "Sua sessão acabou. Entre de novo para continuar.",
    FORBIDDEN: "Sua conta não tem permissão para isso.",
    NOT_FOUND: "Não encontrei o que você pediu.",
    EMAIL_TAKEN: "Já existe uma conta com esse e-mail.",
    ALPHA_KEY_INVALID: "Essa chave de acesso não existe ou já foi usada.",
    INVALID_CREDENTIALS: "E-mail ou senha não conferem.",
    EMAIL_NOT_VERIFIED: "Confirme seu e-mail antes de entrar.",
    TOKEN_EXPIRED: "Sua sessão expirou. Entre de novo.",
    ENTITLEMENT_REQUIRED: "Isso faz parte de um item que sua conta ainda não tem.",
    LEAGUE_FULL: "Essa liga já está cheia.",
    LEAGUE_STARTED: "Essa liga já começou e não aceita mais gente.",
    DEADLINE_PASSED: "O prazo da rodada acabou. Suas ordens valem para a próxima.",
    ROUND_PROCESSING: "A rodada está sendo processada. Tente de novo em instantes.",
    NOT_MEMBER: "Você não está nessa liga.",
    NOT_COMMISSIONER: "Só o comissário da liga pode fazer isso.",
    INVALID_LINEUP: "Essa escalação não é válida.",
    WINDOW_CLOSED: "A janela de transferências está fechada.",
    INSUFFICIENT_FUNDS: "O caixa do clube não cobre esse valor.",
    OFFER_BELOW_MINIMUM: "A proposta está abaixo do mínimo que o dono aceita.",
    TRADE_COOLDOWN: "Espere um pouco antes de negociar de novo com esse clube.",
    PLAYER_UNAVAILABLE: "Esse jogador não está disponível agora.",
    RATE_LIMITED: "Você fez pedidos demais em pouco tempo. Espere um instante.",
    ENGINE_VERSION_MISMATCH: "Este aplicativo está desatualizado. Atualize para continuar.",
    CONFLICT: "Outra pessoa mexeu nisso antes de você. Recarregue e tente de novo.",
    PAYLOAD_TOO_LARGE: "O arquivo passa do tamanho aceito.",
    BILLING_NOT_CONFIGURED: "A loja está fora do ar por enquanto. Tente mais tarde.",
    LINKED_ACCOUNT: "Outra conta ligada a esta já está nessa liga.",
    NATIONS_WINDOW_CLOSED: "A convocação só abre no ano da Copa das Nações.",
    REDE: "Não consegui falar com o servidor. Confira sua conexão.",
    INTERNAL: "Algo deu errado no servidor. Tente de novo em instantes.",
  },
} as const;
